// src/components/dashboards/HostelDashboard.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../shared/DashboardLayout';
import StatCard from '../shared/StatCard';
import { hostelInventoryAPI, departmentAPI } from '../../services/api';
import api from '../../services/api';
import Calendar from '../shared/Calendar';
import { notify, confirmDialog } from '../../utils/notify';
import '../../styles/Dashboard.css';

const HostelDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const isStaff = user?.role === 'ADMIN' || user?.role === 'HOSTEL_ADMIN' || user?.role === 'STORE_MANAGER';
  const isStudent = user?.role === 'STUDENT';
  const isStoreOnly = user?.role === 'STORE_MANAGER';

  // State Management
  const [activeTab, setActiveTab] = useState(isStaff ? (isStoreOnly ? 'assets' : 'overview') : 'student-overview');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(null); // 'hostel' | 'building' | 'floor' | 'room' | 'allocation' | 'visitor' | 'leave' | 'complaint' | 'fee' | 'vendor' | 'asset' | 'pr' | 'po' | 'stock' | 'audit'

  // Data States
  const [hostelStats, setHostelStats] = useState(null);
  const [invStats, setInvStats] = useState(null);
  const [hostels, setHostels] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [floors, setFloors] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [visitors, setVisitors] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [outpasses, setOutpasses] = useState([]);
  const [outpassFilterStatus, setOutpassFilterStatus] = useState('ACTIVE');
  const [outpassSearchQuery, setOutpassSearchQuery] = useState('');
  const [activeLeaveSubTab, setActiveLeaveSubTab] = useState('outpasses');
  const [complaints, setComplaints] = useState([]);
  const [fees, setFees] = useState([]);
  const [settings, setSettings] = useState(null);
  
  // Hostel Asset & Purchase States
  const [hostelPurchaseRequests, setHostelPurchaseRequests] = useState([]);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [selectedHpr, setSelectedHpr] = useState(null);
  const [repairForm, setRepairForm] = useState({ repair_started_date: new Date().toISOString().split('T')[0], repair_remarks: '' });
  const [repairCompleteForm, setRepairCompleteForm] = useState({ repair_completed_date: new Date().toISOString().split('T')[0], repair_remarks: '', resolution_notes: 'Repaired successfully.' });
  const [replacementForm, setReplacementForm] = useState({ accessory_type: '', quantity: 1, reason: '', remarks: '', priority: 'MEDIUM' });
  const [installForm, setInstallForm] = useState({ new_asset_label: '', remarks: '', resolution_notes: '' });

  
  // Inventory Data States
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [maintenance, setMaintenance] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [purchaseRequests, setPurchaseRequests] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [stockTransactions, setStockTransactions] = useState([]);
  const [consumables, setConsumables] = useState([]);
  const [consumableAlerts, setConsumableAlerts] = useState({ low_stock: [], expiring_soon: [] });
  const [deptStocks, setDeptStocks] = useState([]);
  const [audits, setAudits] = useState([]);
  const [departments, setDepartments] = useState([]);

  // Student specific
  const [studentProfile, setStudentProfile] = useState(null);

  // Filter States
  const [roomFilter, setRoomFilter] = useState({ hostel: '', status: '', type: '' });
  const [allocFilter, setAllocFilter] = useState({ status: 'ACTIVE' });
  const [attendanceDate, setAttendanceDate] = useState(new Date().toISOString().split('T')[0]);
  const [visitorFilterDate, setVisitorFilterDate] = useState('');
  const [leaveStatusFilter, setLeaveStatusFilter] = useState('');
  const [complaintStatusFilter, setComplaintStatusFilter] = useState('');
  const [feeStatusFilter, setFeeStatusFilter] = useState('');
  const [itemSearch, setItemSearch] = useState('');
  const [itemFilter, setItemFilter] = useState({ status: '', category: '', department: '', condition: '' });

  // Form States
  const [hostelForm, setHostelForm] = useState({ name: '', campus: '', hostel_type: 'BOYS', total_capacity: '', contact_phone: '', contact_email: '', facilities: '', rules: '' });
  const [buildingForm, setBuildingForm] = useState({ hostel: '', name: '', wing: '', floors_count: '1' });
  const [floorForm, setFloorForm] = useState({ building: '', floor_number: '', floor_name: '' });
  const [roomForm, setRoomForm] = useState({
    floor: '',
    room_number: '',
    room_type: 'DOUBLE',
    capacity: '2',
    has_ac: false,
    has_wifi: true,
    has_attached_bath: false,
    monthly_rent: '',
    notes: '',
    asset_ids: []
  });
  const [availableAssets, setAvailableAssets] = useState([]);
  const [roomAssetSelections, setRoomAssetSelections] = useState({});
  const [isEditingRoom, setIsEditingRoom] = useState(false);
  const [editRoomForm, setEditRoomForm] = useState({ room_number: '', room_type: 'DOUBLE', capacity: '2', monthly_rent: '', has_ac: false, has_wifi: true, has_attached_bath: false, status: 'AVAILABLE', notes: '' });
  const [swappingAsset, setSwappingAsset] = useState(null);
  const [swapOptions, setSwapOptions] = useState([]);
  const [selectedSwapTarget, setSelectedSwapTarget] = useState('');
  const [hostelPrForm, setHostelPrForm] = useState({
    hostel: '',
    accessory_type: 'BED',
    quantity: 1,
    priority: 'MEDIUM',
    reason: '',
    remarks: ''
  });
  const [allocForm, setAllocForm] = useState({ room: '', resident_username: '', security_deposit: '', notes: '' });
  const [visitorForm, setVisitorForm] = useState({ student_username: '', visitor_name: '', visitor_mobile: '', id_proof_type: '', id_proof_number: '', relationship: '', purpose: '', entry_time: '' });
  const [leaveForm, setLeaveForm] = useState({ leave_type: 'DAY', from_date: '', to_date: '', from_time: '', to_time: '', destination: '', reason: '', parent_contact: '' });
  const [complaintForm, setComplaintForm] = useState({ title: '', room: '', category: 'ELECTRICAL', description: '', priority: 'MEDIUM' });
  const [selectedRoomDetails, setSelectedRoomDetails] = useState(null);
  const [roomDetailsTab, setRoomDetailsTab] = useState('allocations');
  const [selectedAssetForLog, setSelectedAssetForLog] = useState(null);
  const [feeForm, setFeeForm] = useState({ resident_username: '', room: '', fee_type: 'HOSTEL', amount: '', due_date: '', month_year: '', notes: '' });
  const [vendorForm, setVendorForm] = useState({ name: '', contact_person: '', email: '', phone: '', address: '', gst_number: '', payment_terms: '', notes: '' });
  const [assetForm, setAssetForm] = useState({ name: '', asset_code: '', barcode: '', category: '', department: '', purchase_date: '', vendor: '', purchase_price: '', warranty_expiry: '', amc_expiry: '', condition: 'NEW', current_location: '', description: '', depreciation_rate: '0', quantity_total: '1', is_consumable: false });
  const [prForm, setPrForm] = useState({ item_name: '', category: '', quantity: '1', unit: 'pcs', justification: '', estimated_cost: '', priority: 'MEDIUM', department: '' });
  const [poForm, setPoForm] = useState({ request: '', vendor: '', po_number: '', items: [{ name: '', qty: 1, unit_price: 0, total: 0 }], total_amount: 0, expected_delivery: '', notes: '' });
  const [stockForm, setStockForm] = useState({ item: '', transaction_type: 'IN', quantity: '', from_location: '', to_location: '', reference_number: '', notes: '' });
  const [auditForm, setAuditForm] = useState({ item: '', audit_date: '', expected_quantity: '', actual_quantity: '', notes: '' });

  // Autocomplete Resident Student Search States
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSuggestions, setUserSuggestions] = useState([]);
  const [selectedResidentUser, setSelectedResidentUser] = useState(null);
  const [isSearchingUsers, setIsSearchingUsers] = useState(false);

  // Autocomplete Visitor Student Search States
  const [visitorSearchQuery, setVisitorSearchQuery] = useState('');
  const [visitorSuggestions, setVisitorSuggestions] = useState([]);
  const [selectedVisitorUser, setSelectedVisitorUser] = useState(null);
  const [isSearchingVisitors, setIsSearchingVisitors] = useState(false);

  // Autocomplete Outpass Student Search States
  const [outpassUserSearchQuery, setOutpassUserSearchQuery] = useState('');
  const [outpassUserSuggestions, setOutpassUserSuggestions] = useState([]);
  const [selectedOutpassUser, setSelectedOutpassUser] = useState(null);
  const [isSearchingOutpassUsers, setIsSearchingOutpassUsers] = useState(false);

  // Autocomplete Handover User Search States
  const [handoverSearchQuery, setHandoverSearchQuery] = useState('');
  const [handoverSuggestions, setHandoverSuggestions] = useState([]);
  const [selectedHandoverUser, setSelectedHandoverUser] = useState(null);
  const [isSearchingHandoverUsers, setIsSearchingHandoverUsers] = useState(false);

  const [outpassForm, setOutpassForm] = useState({
    student_username: '',
    out_date: '',
    out_time: '',
    expected_return_date: '',
    expected_return_time: '',
    reason: ''
  });

  useEffect(() => {
    if (showModal !== 'allocation') {
      setUserSearchQuery('');
      setUserSuggestions([]);
      setSelectedResidentUser(null);
    }
    if (showModal !== 'visitor') {
      setVisitorSearchQuery('');
      setVisitorSuggestions([]);
      setSelectedVisitorUser(null);
    }
    if (showModal !== 'outpass') {
      setOutpassUserSearchQuery('');
      setOutpassUserSuggestions([]);
      setSelectedOutpassUser(null);
    }
  }, [showModal]);

  const handleUserSearchChange = async (val) => {
    setUserSearchQuery(val);
    setSelectedResidentUser(null);
    setAllocForm(prev => ({ ...prev, resident_username: '' }));

    if (val.trim().length === 0) {
      setUserSuggestions([]);
      return;
    }

    setIsSearchingUsers(true);
    try {
      const data = await hostelInventoryAPI.searchResidents(val, true);
      setUserSuggestions(data);
    } catch (err) {
      console.error('Failed to search residents', err);
    } finally {
      setIsSearchingUsers(false);
    }
  };

  const handleUserSearchFocus = async () => {
    if (userSearchQuery.trim() === '') {
      setIsSearchingUsers(true);
      try {
        const data = await hostelInventoryAPI.searchResidents('', true);
        setUserSuggestions(data);
      } catch (err) {
        console.error('Failed to fetch residents', err);
      } finally {
        setIsSearchingUsers(false);
      }
    }
  };

  const handleUserSearchBlur = () => {
    setTimeout(() => {
      setUserSuggestions([]);
    }, 200);
  };

  const handleSelectSuggestion = (userObj) => {
    setSelectedResidentUser(userObj);
    setUserSearchQuery(`${userObj.name} (${userObj.username})`);
    setAllocForm(prev => ({ ...prev, resident_username: userObj.username }));
    setUserSuggestions([]);
  };

  const handleVisitorSearchChange = async (val) => {
    setVisitorSearchQuery(val);
    setSelectedVisitorUser(null);
    setVisitorForm(prev => ({ ...prev, student_username: '' }));

    if (val.trim().length === 0) {
      setVisitorSuggestions([]);
      return;
    }

    setIsSearchingVisitors(true);
    try {
      const data = await hostelInventoryAPI.searchResidents(val, false, true);
      setVisitorSuggestions(data);
    } catch (err) {
      console.error('Failed to search visitors', err);
    } finally {
      setIsSearchingVisitors(false);
    }
  };

  const handleVisitorSearchFocus = async () => {
    if (visitorSearchQuery.trim() === '') {
      setIsSearchingVisitors(true);
      try {
        const data = await hostelInventoryAPI.searchResidents('', false, true);
        setVisitorSuggestions(data);
      } catch (err) {
        console.error('Failed to fetch visitors', err);
      } finally {
        setIsSearchingVisitors(false);
      }
    }
  };

  const handleVisitorSearchBlur = () => {
    setTimeout(() => {
      setVisitorSuggestions([]);
    }, 200);
  };

  const handleSelectVisitorSuggestion = (userObj) => {
    setSelectedVisitorUser(userObj);
    setVisitorSearchQuery(`${userObj.name} (${userObj.username})`);
    setVisitorForm(prev => ({ ...prev, student_username: userObj.username }));
    setVisitorSuggestions([]);
  };

  const handleOutpassUserSearchChange = async (val) => {
    setOutpassUserSearchQuery(val);
    setSelectedOutpassUser(null);
    setOutpassForm(prev => ({ ...prev, student_username: '' }));

    if (val.trim().length === 0) {
      setOutpassUserSuggestions([]);
      return;
    }

    setIsSearchingOutpassUsers(true);
    try {
      const data = await hostelInventoryAPI.searchResidents(val, false, true);
      setOutpassUserSuggestions(data);
    } catch (err) {
      console.error('Failed to search outpass users', err);
    } finally {
      setIsSearchingOutpassUsers(false);
    }
  };

  const handleOutpassUserSearchFocus = async () => {
    if (outpassUserSearchQuery.trim() === '') {
      setIsSearchingOutpassUsers(true);
      try {
        const data = await hostelInventoryAPI.searchResidents('', false, true);
        setOutpassUserSuggestions(data);
      } catch (err) {
        console.error('Failed to fetch outpass users', err);
      } finally {
        setIsSearchingOutpassUsers(false);
      }
    }
  };

  const handleOutpassUserSearchBlur = () => {
    setTimeout(() => {
      setOutpassUserSuggestions([]);
    }, 200);
  };

  const handleSelectOutpassUserSuggestion = (userObj) => {
    setSelectedOutpassUser(userObj);
    setOutpassUserSearchQuery(`${userObj.name} (${userObj.username})`);
    setOutpassForm(prev => ({ ...prev, student_username: userObj.username }));
    setOutpassUserSuggestions([]);
  };

  const handleHandoverSearchChange = async (val) => {
    setHandoverSearchQuery(val);
    setSelectedHandoverUser(null);
    setAllocForm(prev => ({ ...prev, resident_username: '' }));

    if (val.trim().length === 0) {
      setHandoverSuggestions([]);
      return;
    }

    setIsSearchingHandoverUsers(true);
    try {
      const selectedAssetId = roomForm.floor;
      const selectedAsset = items.find(i => String(i.id) === String(selectedAssetId));
      const hostelId = selectedAsset ? (selectedAsset.hostel_id || selectedAsset.hostel) : null;
      const data = await hostelInventoryAPI.searchResidents(val, false, true, hostelId);
      setHandoverSuggestions(data);
    } catch (err) {
      console.error('Failed to search handover users', err);
    } finally {
      setIsSearchingHandoverUsers(false);
    }
  };

  const handleHandoverSearchFocus = async () => {
    if (handoverSearchQuery.trim() === '') {
      setIsSearchingHandoverUsers(true);
      try {
        const selectedAssetId = roomForm.floor;
        const selectedAsset = items.find(i => String(i.id) === String(selectedAssetId));
        const hostelId = selectedAsset ? (selectedAsset.hostel_id || selectedAsset.hostel) : null;
        const data = await hostelInventoryAPI.searchResidents('', false, true, hostelId);
        setHandoverSuggestions(data);
      } catch (err) {
        console.error('Failed to fetch handover users', err);
      } finally {
        setIsSearchingHandoverUsers(false);
      }
    }
  };

  const handleHandoverSearchBlur = () => {
    setTimeout(() => {
      setHandoverSuggestions([]);
    }, 200);
  };

  const handleSelectHandoverSuggestion = (userObj) => {
    setSelectedHandoverUser(userObj);
    setHandoverSearchQuery(`${userObj.name} (${userObj.username})`);
    setAllocForm(prev => ({ ...prev, resident_username: userObj.username }));
    setHandoverSuggestions([]);
  };

  useEffect(() => {
    if (showModal === 'assignment') {
      setHandoverSearchQuery('');
      setHandoverSuggestions([]);
      setSelectedHandoverUser(null);
      setAllocForm(prev => ({ ...prev, resident_username: '' }));
    }
    // Reset asset selections and room edit state whenever modal changes
    setRoomAssetSelections({});
    setIsEditingRoom(false);
  }, [showModal]);

  // Load basic helper data
  useEffect(() => {
    departmentAPI.getDepartments().then(res => setDepartments(res.results || res)).catch(console.error);
    hostelInventoryAPI.getCategories().then(res => setCategories(res.results || res)).catch(console.error);
    hostelInventoryAPI.getVendors().then(res => setVendors(res.results || res)).catch(console.error);
    hostelInventoryAPI.getHostels().then(res => setHostels(res.results || res)).catch(console.error);
  }, []);

  // Main data fetch based on tab
  useEffect(() => {
    fetchData();
  }, [activeTab, roomFilter, allocFilter, attendanceDate, visitorFilterDate, leaveStatusFilter, complaintStatusFilter, feeStatusFilter, itemSearch, itemFilter, outpassFilterStatus, outpassSearchQuery]);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      if (isStaff) {
        if (activeTab === 'overview') {
          const stats = await hostelInventoryAPI.getHostelStats();
          setHostelStats(stats);
          const recentComplaints = await hostelInventoryAPI.getComplaints({ status: 'OPEN' });
          setComplaints(recentComplaints.results || recentComplaints);
          const activeAllocations = await hostelInventoryAPI.getAllocations({ status: 'ACTIVE' });
          setAllocations(activeAllocations.results || activeAllocations);
        } else if (activeTab === 'hostels') {
          const res = await hostelInventoryAPI.getHostels();
          setHostels(res.results || res);
        } else if (activeTab === 'rooms') {
          const res = await hostelInventoryAPI.getRooms({
            hostel: roomFilter.hostel,
            status: roomFilter.status,
            type: roomFilter.type
          });
          setRooms(res.results || res);
          const hostList = await hostelInventoryAPI.getHostels();
          setHostels(hostList.results || hostList);
        } else if (activeTab === 'allocations') {
          const res = await hostelInventoryAPI.getAllocations(allocFilter);
          setAllocations(res.results || res);
          const rList = await hostelInventoryAPI.getRooms({ status: 'AVAILABLE' });
          setRooms(rList.results || rList);
        } else if (activeTab === 'attendance') {
          const res = await hostelInventoryAPI.getAttendance({ date: attendanceDate });
          setAttendance(res.results || res);
        } else if (activeTab === 'visitors') {
          const res = await hostelInventoryAPI.getVisitors({ date: visitorFilterDate });
          setVisitors(res.results || res);
        } else if (activeTab === 'leaves') {
          const res = await hostelInventoryAPI.getLeaves({ status: leaveStatusFilter });
          setLeaves(res.results || res);
          const outList = await hostelInventoryAPI.getOutpasses({ status: outpassFilterStatus, search: outpassSearchQuery });
          setOutpasses(outList.results || outList);
        } else if (activeTab === 'complaints') {
          const res = await hostelInventoryAPI.getComplaints({ status: complaintStatusFilter });
          setComplaints(res.results || res);
        } else if (activeTab === 'fees') {
          const res = await hostelInventoryAPI.getFees({ status: feeStatusFilter });
          setFees(res.results || res);
        } else if (activeTab === 'settings') {
          const res = await hostelInventoryAPI.getSettings();
          setSettings(res);
        } else if (activeTab === 'assets') {
          const res = await hostelInventoryAPI.getGlobalAssets({
            availability_status: 'AVAILABLE',
            hostel_id: roomFilter.hostel,
            asset_type: itemFilter.category,
            search: itemSearch
          });
          setItems(res.results || res);
          try {
            const hostList = await hostelInventoryAPI.getHostels();
            setHostels(hostList.results || hostList);
          } catch (e) {
            console.error('Failed to fetch hostels', e);
          }
        } else if (activeTab === 'purchases') {
          try {
            const hprs = await hostelInventoryAPI.getHostelPurchaseRequests();
            setHostelPurchaseRequests(hprs.results || hprs);
            const hostList = await hostelInventoryAPI.getHostels();
            setHostels(hostList.results || hostList);
          } catch (e) {
            console.error('Failed to fetch hostel purchase requests or hostels', e);
          }
        }
      }

      if (isStudent) {
        if (activeTab === 'student-overview') {
          const profile = await hostelInventoryAPI.getMyHostelProfile();
          setStudentProfile(profile);
        } else if (activeTab === 'student-leaves') {
          const res = await hostelInventoryAPI.getLeaves();
          setLeaves(res.results || res);
        } else if (activeTab === 'student-complaints') {
          const res = await hostelInventoryAPI.getComplaints();
          setComplaints(res.results || res);
          const profile = await hostelInventoryAPI.getMyHostelProfile();
          setStudentProfile(profile);
        } else if (activeTab === 'student-fees') {
          const res = await hostelInventoryAPI.getFees();
          setFees(res.results || res);
        } else if (activeTab === 'student-assets') {
          const res = await hostelInventoryAPI.getAssignments();
          setAssignments(res.results || res);
        }
      }
    } catch (err) {
      setError(err.response?.data ? JSON.stringify(err.response.data) : 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    if (!user) {
      navigate('/login');
      return;
    }
    switch (user.role) {
      case 'ADMIN':
        navigate('/admin-dashboard');
        break;
      case 'STUDENT':
        navigate('/student-dashboard');
        break;
      case 'FACULTY':
        navigate(user.subrole === 'HEAD_OF_DEPARTMENT' ? '/hod-dashboard' : '/faculty-dashboard');
        break;
      case 'PRINCIPAL':
        navigate('/principal-dashboard');
        break;
      case 'DEAN':
        navigate('/dean-dashboard');
        break;
      case 'FINANCE':
        navigate('/finance-dashboard');
        break;
      case 'HOSTEL_ADMIN':
        navigate('/hostel-dashboard');
        break;
      default:
        window.history.back();
    }
  };

  // --- SUBMISSIONS ---

  const handleHostelSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...hostelForm, facilities: hostelForm.facilities.split(',').map(x => x.trim()) };
      await hostelInventoryAPI.createHostel(data);
      setSuccess('Hostel created successfully');
      setShowModal(null);
      fetchData();
    } catch (err) {
      setError('Failed to create hostel');
    }
  };

  const handleBuildingSubmit = async (e) => {
    e.preventDefault();
    try {
      await hostelInventoryAPI.createBuilding(buildingForm);
      setSuccess('Building created successfully');
      setShowModal(null);
      fetchData();
    } catch (err) {
      setError('Failed to create building');
    }
  };

  const handleFloorSubmit = async (e) => {
    e.preventDefault();
    try {
      await hostelInventoryAPI.createFloor(floorForm);
      setSuccess('Floor created successfully');
      setShowModal(null);
      fetchData();
    } catch (err) {
      setError('Failed to create floor');
    }
  };

  const handleRoomSubmit = async (e) => {
    e.preventDefault();
    try {
      await hostelInventoryAPI.createRoom(roomForm);
      setSuccess('Room created successfully');
      setShowModal(null);
      setRoomForm({
        floor: '',
        room_number: '',
        room_type: 'DOUBLE',
        capacity: '2',
        has_ac: false,
        has_wifi: true,
        has_attached_bath: false,
        monthly_rent: '',
        notes: '',
        asset_ids: []
      });
      setAvailableAssets([]);
      setRoomAssetSelections({});
      fetchData();
    } catch (err) {
      setError(err.response?.data ? JSON.stringify(err.response.data) : 'Failed to create room');
    }
  };

  const handleEditRoomSubmit = async (e) => {
    e.preventDefault();
    if (!selectedRoomDetails?.room?.id) return;
    try {
      await hostelInventoryAPI.updateRoom(selectedRoomDetails.room.id, editRoomForm);
      setSuccess('Room updated successfully');
      setIsEditingRoom(false);
      // Refresh room details
      const data = await hostelInventoryAPI.getRoomDetail(selectedRoomDetails.room.id);
      setSelectedRoomDetails(data);
      fetchData();
    } catch (err) {
      setError(err.response?.data ? JSON.stringify(err.response.data) : 'Failed to update room');
    }
  };

  const handleViewRoomDetails = async (roomId) => {
    try {
      setLoading(true);
      const data = await hostelInventoryAPI.getRoomDetail(roomId);
      setSelectedRoomDetails(data);
      setRoomDetailsTab('allocations');
      setSelectedAssetForLog(null);
      setShowModal('roomDetail');
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setError('Failed to load room details');
    }
  };

  const handleUpdateAssetStatus = async (assetId, newStatus) => {
    try {
      await hostelInventoryAPI.updateAssetStatus(assetId, { status: newStatus });
      setSuccess('Asset status updated successfully');
      if (selectedRoomDetails?.room?.id) {
        const data = await hostelInventoryAPI.getRoomDetail(selectedRoomDetails.room.id);
        setSelectedRoomDetails(data);
      }
    } catch (err) {
      setError('Failed to update asset status');
    }
  };

  const handleViewAssetLog = (asset) => {
    setSelectedAssetForLog(asset);
    setRoomDetailsTab('events');
  };

  const handleAllocationSubmit = async (e) => {
    e.preventDefault();
    if (!selectedResidentUser) {
      setError('Please select a valid registered student from the search suggestions.');
      return;
    }
    try {
      await hostelInventoryAPI.createAllocation({
        room: allocForm.room,
        resident: selectedResidentUser.id,
        security_deposit: allocForm.security_deposit,
        notes: allocForm.notes
      });
      setSuccess('Room allocated successfully');
      setShowModal(null);
      setSelectedResidentUser(null);
      setUserSearchQuery('');
      setUserSuggestions([]);
      setAllocFilter({ status: 'ACTIVE' });
      fetchData();
    } catch (err) {
      setError(err.response?.data ? JSON.stringify(err.response.data) : 'Failed to allocate room');
    }
  };

  const handleVisitorSubmit = async (e) => {
    e.preventDefault();
    if (!selectedVisitorUser) {
      setError('Please select a valid student from the search suggestions.');
      return;
    }
    try {
      await hostelInventoryAPI.addVisitor({
        student: selectedVisitorUser.id,
        visitor_name: visitorForm.visitor_name,
        visitor_mobile: visitorForm.visitor_mobile,
        id_proof_type: visitorForm.id_proof_type,
        id_proof_number: visitorForm.id_proof_number,
        relationship: visitorForm.relationship,
        purpose: visitorForm.purpose,
        entry_time: visitorForm.entry_time || new Date().toISOString()
      });
      setSuccess('Visitor recorded successfully');
      setShowModal(null);
      setSelectedVisitorUser(null);
      setVisitorSearchQuery('');
      setVisitorSuggestions([]);
      fetchData();
    } catch (err) {
      setError('Failed to log visitor');
    }
  };

  const handleOutpassSubmit = async (e) => {
    e.preventDefault();
    if (!selectedOutpassUser) {
      setError('Please select a valid student from the search suggestions.');
      return;
    }
    try {
      await hostelInventoryAPI.createOutpass({
        student: selectedOutpassUser.id,
        out_date: outpassForm.out_date,
        out_time: outpassForm.out_time,
        expected_return_date: outpassForm.expected_return_date,
        expected_return_time: outpassForm.expected_return_time,
        reason: outpassForm.reason
      });
      setSuccess('Outpass ticket issued successfully');
      setShowModal(null);
      setSelectedOutpassUser(null);
      setOutpassUserSearchQuery('');
      setOutpassUserSuggestions([]);
      setOutpassForm({
        student_username: '',
        out_date: '',
        out_time: '',
        expected_return_date: '',
        expected_return_time: '',
        reason: ''
      });
      setOutpassFilterStatus('ACTIVE');
      fetchData();
    } catch (err) {
      setError(err.response?.data ? JSON.stringify(err.response.data) : 'Failed to issue outpass');
    }
  };

  const handleCloseOutpass = async (id) => {
    if (!await confirmDialog('Are you sure this resident has returned to the hostel? This will close the outpass ticket.')) {
      return;
    }
    try {
      await hostelInventoryAPI.closeOutpass(id);
      setSuccess('Outpass ticket closed successfully');
      fetchData();
    } catch (err) {
      setError('Failed to close outpass ticket');
    }
  };

  const handleLeaveSubmit = async (e) => {
    e.preventDefault();
    try {
      await hostelInventoryAPI.applyLeave(leaveForm);
      setSuccess('Leave request submitted successfully');
      setShowModal(null);
      fetchData();
    } catch (err) {
      setError('Failed to submit leave request');
    }
  };

  const handleComplaintSubmit = async (e) => {
    e.preventDefault();
    try {
      await hostelInventoryAPI.submitComplaint({
        title: complaintForm.title,
        room: complaintForm.room || studentProfile?.allocation?.room,
        category: complaintForm.category,
        description: complaintForm.description,
        priority: complaintForm.priority
      });
      setSuccess('Complaint filed successfully');
      setShowModal(null);
      setComplaintForm({ title: '', room: '', category: 'ELECTRICAL', description: '', priority: 'MEDIUM' });
      fetchData();
    } catch (err) {
      setError('Failed to file complaint');
    }
  };

  const handleFeeSubmit = async (e) => {
    e.preventDefault();
    try {
      const userRes = await api.get(`/auth/principal/users/?search=${feeForm.resident_username}`);
      const matchedUser = userRes.data.results?.find(u => u.username === feeForm.resident_username);
      if (!matchedUser) {
        setError('Resident username not found');
        return;
      }
      await hostelInventoryAPI.createFee({
        resident: matchedUser.id,
        room: feeForm.room || null,
        fee_type: feeForm.fee_type,
        amount: feeForm.amount,
        due_date: feeForm.due_date,
        month_year: feeForm.month_year,
        notes: feeForm.notes
      });
      setSuccess('Fee bill generated successfully');
      setShowModal(null);
      fetchData();
    } catch (err) {
      setError('Failed to generate fee record');
    }
  };

  const handleVendorSubmit = async (e) => {
    e.preventDefault();
    try {
      await hostelInventoryAPI.createVendor(vendorForm);
      setSuccess('Vendor registered successfully');
      setShowModal(null);
      fetchData();
    } catch (err) {
      setError('Failed to register vendor');
    }
  };

  const handleAssetSubmit = async (e) => {
    e.preventDefault();
    try {
      const fd = new FormData();
      Object.keys(assetForm).forEach(k => {
        if (assetForm[k] !== null && assetForm[k] !== '') {
          fd.append(k, assetForm[k]);
        }
      });
      await hostelInventoryAPI.createItem(fd);
      setSuccess('Asset registered successfully');
      setShowModal(null);
      fetchData();
    } catch (err) {
      setError('Failed to register asset');
    }
  };

  const handleHostelPrSubmit = async (e) => {
    e.preventDefault();
    try {
      await hostelInventoryAPI.createHostelPurchaseRequest(hostelPrForm);
      setSuccess('Hostel accessory procurement request submitted successfully');
      setShowModal(null);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit procurement request');
    }
  };

  const handleUnallocateAsset = async (assetId) => {
    if (await confirmDialog('Are you sure you want to unallocate this asset from this room and return it to inventory?')) {
      try {
        await hostelInventoryAPI.updateAssetStatus(assetId, {
          room: null,
          availability_status: 'AVAILABLE',
          accessory: null
        });
        setSuccess('Asset unallocated and returned to inventory successfully.');
        // Refresh room details
        const data = await hostelInventoryAPI.getRoomDetail(selectedRoomDetails.room.id);
        setSelectedRoomDetails(data);
        fetchData();
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to unallocate asset.');
      }
    }
  };

  const handleInitiateSwap = async (asset) => {
    try {
      const res = await hostelInventoryAPI.getGlobalAssets({
        availability_status: 'AVAILABLE',
        asset_type: asset.asset_type,
        hostel_id: selectedRoomDetails.room.hostel_id || selectedRoomDetails.room.hostel
      });
      setSwapOptions(res.results || res);
      setSwappingAsset(asset);
      setSelectedSwapTarget('');
      setShowModal('swapAsset');
    } catch (err) {
      setError('Failed to fetch replacement assets from inventory.');
    }
  };

  const handleCompleteSwap = async (e) => {
    e.preventDefault();
    if (!selectedSwapTarget) return;
    try {
      // 1. Unallocate old asset
      await hostelInventoryAPI.updateAssetStatus(swappingAsset.id, {
        room: null,
        availability_status: 'AVAILABLE',
        accessory: null
      });
      // 2. Allocate new asset to this room
      await hostelInventoryAPI.updateAssetStatus(selectedSwapTarget, {
        room: selectedRoomDetails.room.id,
        availability_status: 'ALLOCATED'
      });
      setSuccess('Asset swapped successfully.');
      setShowModal('roomDetail');
      // Refresh room details
      const data = await hostelInventoryAPI.getRoomDetail(selectedRoomDetails.room.id);
      setSelectedRoomDetails(data);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to swap asset.');
    }
  };

  const handlePoSubmit = async (e) => {
    e.preventDefault();
    try {
      await hostelInventoryAPI.createPurchaseOrder(poForm);
      setSuccess('Purchase Order generated successfully');
      setShowModal(null);
      fetchData();
    } catch (err) {
      setError('Failed to create purchase order');
    }
  };

  const handleStockSubmit = async (e) => {
    e.preventDefault();
    try {
      await hostelInventoryAPI.addStockTransaction(stockForm);
      setSuccess('Stock transaction logged successfully');
      setShowModal(null);
      fetchData();
    } catch (err) {
      setError('Failed to log stock transaction');
    }
  };

  const handleAuditSubmit = async (e) => {
    e.preventDefault();
    try {
      await hostelInventoryAPI.createAudit(auditForm);
      setSuccess('Audit record logged successfully');
      setShowModal(null);
      fetchData();
    } catch (err) {
      setError('Failed to log audit');
    }
  };

  const handleApproveLeave = async (id, statusAction) => {
    try {
      await hostelInventoryAPI.approveLeave(id, { action: statusAction });
      setSuccess(`Leave ${statusAction.toLowerCase()}d successfully`);
      fetchData();
    } catch (err) {
      setError('Failed to process leave');
    }
  };

  const handleResolveComplaint = async (id, notes) => {
    try {
      await hostelInventoryAPI.updateComplaint(id, { status: 'RESOLVED', resolution_notes: notes || 'Resolved' });
      setSuccess('Complaint resolved successfully');
      fetchData();
    } catch (err) {
      setError('Failed to resolve complaint');
    }
  };

  const handleMarkComplaintDamaged = async (id) => {
    try {
      await hostelInventoryAPI.markComplaintDamaged(id);
      setSuccess('Asset marked as DAMAGED and complaint status updated.');
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to mark asset as damaged');
    }
  };

  const handleStartComplaintRepair = async (id, data) => {
    try {
      await hostelInventoryAPI.startComplaintRepair(id, data);
      setSuccess('Repair work started successfully.');
      setShowModal(null);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start repair');
    }
  };

  const handleCompleteComplaintRepair = async (id, data) => {
    try {
      await hostelInventoryAPI.completeComplaintRepair(id, data);
      setSuccess('Repair work completed and complaint resolved.');
      setShowModal(null);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to complete repair');
    }
  };

  const handleRequestComplaintReplacement = async (id, data) => {
    try {
      await hostelInventoryAPI.requestComplaintReplacement(id, data);
      setSuccess('Replacement purchase request generated successfully.');
      setShowModal(null);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to request replacement');
    }
  };

  const handleApproveHostelPR = async (id, status, reason = '') => {
    try {
      await hostelInventoryAPI.updateHostelPurchaseRequest(id, { status, rejection_reason: reason });
      setSuccess(`Hostel purchase request ${status === 'APPROVED_BY_ADMIN' ? 'approved' : 'rejected'} successfully.`);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update purchase request status');
    }
  };

  const handleInstallHostelAsset = async (id, data) => {
    try {
      await hostelInventoryAPI.installHostelAsset(id, data);
      setSuccess('New asset installed and old asset replaced successfully.');
      setShowModal(null);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to install asset');
    }
  };


  const handleMarkPresent = async (resId, roomId, isPresent) => {
    try {
      await hostelInventoryAPI.markAttendance({
        resident: resId,
        room: roomId,
        date: attendanceDate,
        is_present: isPresent
      });
      fetchData();
    } catch (err) {
      setError('Failed to log attendance');
    }
  };

  const handleRecordFeePayment = async (id, amountPaid, mode, recNum) => {
    try {
      await hostelInventoryAPI.updateFee(id, {
        paid_amount: amountPaid,
        status: 'PAID',
        payment_mode: mode,
        receipt_number: recNum,
        paid_date: new Date().toISOString().split('T')[0]
      });
      setSuccess('Payment recorded successfully');
      fetchData();
    } catch (err) {
      setError('Failed to save fee payment');
    }
  };

  const handleReturnAsset = async (id, condition) => {
    try {
      await hostelInventoryAPI.returnAsset(id, { status: 'RETURNED', return_condition: condition, return_date: new Date().toISOString().split('T')[0] });
      setSuccess('Asset return recorded successfully');
      fetchData();
    } catch (err) {
      setError('Failed to process asset return');
    }
  };

  const handlePrApprove = async (id, action) => {
    try {
      await hostelInventoryAPI.approvePurchaseRequest(id, { action });
      setSuccess(`PR ${action.toLowerCase()}d successfully`);
      fetchData();
    } catch (err) {
      setError('Failed to process PR approval');
    }
  };

  // Helper for loading floors for selected building in rooms form
  const handleBuildingChange = async (buildingId) => {
    setRoomForm(prev => ({ ...prev, floor: '' }));
    try {
      const floorsList = await hostelInventoryAPI.getFloors({ building: buildingId });
      setFloors(floorsList.results || floorsList);
    } catch (e) {
      console.error(e);
    }
  };

  // Helper to load buildings for hostel in buildings/rooms form
  const handleHostelChange = async (hostelId) => {
    setBuildingForm(prev => ({ ...prev, hostel: hostelId }));
    try {
      const bList = await hostelInventoryAPI.getBuildings({ hostel: hostelId });
      setBuildings(bList.results || bList);
    } catch (e) {
      console.error(e);
    }
  };

  const handleRoomHostelChange = async (hostelId) => {
    handleHostelChange(hostelId);
    try {
      const res = await hostelInventoryAPI.getGlobalAssets({
        availability_status: 'AVAILABLE',
        hostel_id: hostelId
      });
      setAvailableAssets(res.results || res);
    } catch (e) {
      console.error('Failed to fetch available assets', e);
    }
  };

  return (
    <DashboardLayout title="Hostel & Inventory Dashboard">
      <div className="dashboard-container">
        
        {/* Alerts */}
        {error && (
          <div className="activity-item activity-warning" style={{ margin: '0 0 20px 0', borderLeft: '4px solid #ef4444' }}>
            <div className="activity-content">
              <h4 style={{ color: '#b91c1c' }}>Error Encountered</h4>
              <p style={{ color: '#7f1d1d' }}>{error}</p>
            </div>
          </div>
        )}
        {success && (
          <div className="activity-item activity-success" style={{ margin: '0 0 20px 0', borderLeft: '4px solid #10b981' }}>
            <div className="activity-content">
              <h4 style={{ color: '#047857' }}>Success</h4>
              <p style={{ color: '#064e3b' }}>{success}</p>
            </div>
          </div>
        )}

        

        {/* --- STATS GRID --- */}
        {isStaff && activeTab === 'overview' && hostelStats && (
          <div className="stats-grid">
            <StatCard title="Total Hostels" value={hostelStats.total_hostels} color="blue" />
            <StatCard title="Occupancy Rate" value={`${hostelStats.total_rooms ? Math.round((hostelStats.occupied_rooms/hostelStats.total_rooms)*100) : 0}%`} color="green" />
            <StatCard title="Vacant Rooms" value={hostelStats.vacant_rooms} color="orange" />
            <StatCard title="Open Complaints" value={hostelStats.open_complaints} color="red" />
          </div>
        )}
        {isStaff && activeTab === 'inv-overview' && invStats && (
          <div className="stats-grid">
            <StatCard title="Total Asset Types" value={invStats.total_items} color="blue" />
            <StatCard title="Assigned Assets" value={invStats.assigned_items} color="purple" />
            <StatCard title="Low Stock Consumables" value={invStats.low_stock_count} color="orange" />
            <StatCard title="Pending PRs" value={invStats.pending_purchase_requests} color="red" />
          </div>
        )}

        {/* --- TABS SYSTEM --- */}
        <div className="tabs-container">
          <div className="tabs">
            {isStaff && !isStoreOnly && (
              <>
                <button className={`tab ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>Overview</button>
                <button className={`tab ${activeTab === 'hostels' ? 'active' : ''}`} onClick={() => setActiveTab('hostels')}>Hostels</button>
                <button className={`tab ${activeTab === 'rooms' ? 'active' : ''}`} onClick={() => setActiveTab('rooms')}>Rooms</button>
                <button className={`tab ${activeTab === 'allocations' ? 'active' : ''}`} onClick={() => setActiveTab('allocations')}>Allocations</button>
                <button className={`tab ${activeTab === 'visitors' ? 'active' : ''}`} onClick={() => setActiveTab('visitors')}>Visitors</button>
                <button className={`tab ${activeTab === 'leaves' ? 'active' : ''}`} onClick={() => setActiveTab('leaves')}>Outpass & Leaves</button>
                <button className={`tab ${activeTab === 'complaints' ? 'active' : ''}`} onClick={() => setActiveTab('complaints')}>Complaints</button>
                <button className={`tab ${activeTab === 'fees' ? 'active' : ''}`} onClick={() => setActiveTab('fees')}>Fees & Billing</button>
              </>
            )}
            {isStaff && (
              <>
                <button className={`tab ${activeTab === 'assets' ? 'active' : ''}`} onClick={() => setActiveTab('assets')}>Hostel Inventory</button>
                <button className={`tab ${activeTab === 'purchases' ? 'active' : ''}`} onClick={() => setActiveTab('purchases')}>Hostel Procurement</button>
              </>
            )}
            {isStudent && (
              <>
                <button className={`tab ${activeTab === 'student-overview' ? 'active' : ''}`} onClick={() => setActiveTab('student-overview')}>My Room Info</button>
                <button className={`tab ${activeTab === 'student-leaves' ? 'active' : ''}`} onClick={() => setActiveTab('student-leaves')}>My Leave Requests</button>
                <button className={`tab ${activeTab === 'student-complaints' ? 'active' : ''}`} onClick={() => setActiveTab('student-complaints')}>File Complaint</button>
                <button className={`tab ${activeTab === 'student-fees' ? 'active' : ''}`} onClick={() => setActiveTab('student-fees')}>My Fees & Dues</button>
                <button className={`tab ${activeTab === 'student-assets' ? 'active' : ''}`} onClick={() => setActiveTab('student-assets')}>My Borrowed Assets</button>
              </>
            )}
            <button className={`tab ${activeTab === 'calendar' ? 'active' : ''}`} onClick={() => setActiveTab('calendar')}>📅 Calendar</button>
          </div>
        </div>

        {/* Loading Indicator */}
        {loading && <div style={{ textAlign: 'center', padding: '40px', fontSize: '18px', color: '#6b7280' }}>Loading system data...</div>}

        {/* --- TAB CONTENT AREA --- */}
        {!loading && (
          <div className="tab-content">

            {/* STAFF OVERVIEW */}
            {isStaff && activeTab === 'overview' && (
              <div className="grid-2-col">
                <div className="card">
                  <div className="card-header">
                    <h2>Pending Leaves & Gatepass</h2>
                    <button className="btn-link" onClick={() => setActiveTab('leaves')}>View All</button>
                  </div>
                  <div className="card-body">
                    {leaves.filter(l => l.status === 'PENDING').slice(0, 5).map(l => (
                      <div key={l.id} className="list-item">
                        <div className="list-item-content">
                          <h4>{l.student_name} ({l.student_username})</h4>
                          <p>{l.leave_type_display} • {l.from_date} to {l.to_date}</p>
                        </div>
                        <div className="action-buttons">
                          <button className="btn-sm btn-success" onClick={() => handleApproveLeave(l.id, 'APPROVE')}>Approve</button>
                          <button className="btn-sm btn-danger" onClick={() => handleApproveLeave(l.id, 'REJECT')}>Reject</button>
                        </div>
                      </div>
                    ))}
                    {leaves.filter(l => l.status === 'PENDING').length === 0 && <p style={{ color: '#6b7280' }}>No pending leave approvals.</p>}
                  </div>
                </div>

                <div className="card">
                  <div className="card-header">
                    <h2>Active Hostels list</h2>
                    <button className="btn-link" onClick={() => setActiveTab('hostels')}>Manage Hostels</button>
                  </div>
                  <div className="card-body">
                    {hostels.slice(0, 5).map(h => (
                      <div key={h.id} className="list-item">
                        <div className="list-item-content">
                          <h4>{h.name}</h4>
                          <p>{h.hostel_type_display} • Capacity: {h.total_capacity}</p>
                        </div>
                        <span className={`badge ${h.is_active ? 'badge-green' : 'badge-red'}`}>{h.is_active ? 'Active' : 'Inactive'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* HOSTELS TAB */}
            {isStaff && activeTab === 'hostels' && (
              <div className="card">
                <div className="card-header">
                  <h2>Hostel Infrastructure Directory</h2>
                  <div className="card-header-actions">
                    <button className="btn-primary" onClick={() => setShowModal('hostel')}>+ Add Hostel</button>
                    <button className="btn-outline" onClick={() => {
                      hostelInventoryAPI.getHostels().then(res => {
                        setHostels(res.results || res);
                        setShowModal('building');
                      });
                    }}>+ Add Building</button>
                    <button className="btn-outline" onClick={() => {
                      hostelInventoryAPI.getHostels().then(async (res) => {
                        const hList = res.results || res;
                        setHostels(hList);
                        if (hList.length > 0) {
                          const bList = await hostelInventoryAPI.getBuildings({ hostel: hList[0].id });
                          setBuildings(bList.results || bList);
                        }
                        setShowModal('floor');
                      });
                    }}>+ Add Floor</button>
                  </div>
                </div>
                <div className="card-body">
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Hostel Name</th>
                          <th>Campus</th>
                          <th>Type</th>
                          <th>Warden</th>
                          <th>Capacity</th>
                          <th>Buildings Count</th>
                        </tr>
                      </thead>
                      <tbody>
                        {hostels.map(h => (
                          <tr key={h.id}>
                            <td><strong>{h.name}</strong></td>
                            <td>{h.campus}</td>
                            <td>{h.hostel_type_display}</td>
                            <td>{h.warden_name || 'Not Assigned'}</td>
                            <td>{h.total_capacity} residents</td>
                            <td>{h.buildings_count} Buildings</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* ROOMS TAB */}
            {isStaff && activeTab === 'rooms' && (
              <div className="card">
                <div className="card-header">
                  <h2>Room Allocations Register</h2>
                  <div className="card-header-actions">
                    <select className="filter-select" onChange={(e) => setRoomFilter(f => ({ ...f, hostel: e.target.value }))}>
                      <option value="">All Hostels</option>
                      {hostels.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
                    </select>
                    <select className="filter-select" onChange={(e) => setRoomFilter(f => ({ ...f, status: e.target.value }))}>
                      <option value="">All Statuses</option>
                      <option value="AVAILABLE">Available</option>
                      <option value="OCCUPIED">Occupied</option>
                      <option value="MAINTENANCE">Maintenance</option>
                    </select>
                    <button className="btn-primary" onClick={async () => {
                      const hList = await hostelInventoryAPI.getHostels();
                      setHostels(hList.results || hList);
                      setShowModal('room');
                    }}>+ Add Room</button>
                  </div>
                </div>
                <div className="card-body">
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Room Number</th>
                          <th>Hostel</th>
                          <th>Building / Floor</th>
                          <th>Type</th>
                          <th>Occupancy</th>
                          <th>Status</th>
                          <th>AC / Attached Bath</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rooms.map(r => (
                          <tr key={r.id}>
                            <td><strong>Room {r.room_number}</strong></td>
                            <td>{r.hostel_name}</td>
                            <td>{r.building_name} — Floor {r.floor_name || r.floor}</td>
                            <td>{r.room_type_display}</td>
                            <td>{r.current_occupancy} / {r.capacity} occupied</td>
                            <td>
                              <span className={`badge ${r.status === 'AVAILABLE' ? 'badge-green' : r.status === 'OCCUPIED' ? 'badge-blue' : 'badge-red'}`}>
                                {r.status_display}
                              </span>
                            </td>
                            <td>{r.has_ac ? '❄️ AC' : 'NO AC'} / {r.has_attached_bath ? '🛀 Attached' : 'Shared'}</td>
                            <td>
                              <button
                                className="btn-primary"
                                style={{ padding: '4px 8px', fontSize: '11px' }}
                                onClick={() => handleViewRoomDetails(r.id)}
                              >
                                View Details
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* ALLOCATIONS TAB */}
            {isStaff && activeTab === 'allocations' && (
              <div className="card">
                <div className="card-header">
                  <h2>Student Room Allocations</h2>
                  <div className="card-header-actions">
                    <select className="filter-select" value={allocFilter.status} onChange={(e) => setAllocFilter({ status: e.target.value })}>
                      <option value="ACTIVE">Active Allocations</option>
                      <option value="VACATED">Vacated History</option>
                      <option value="">All Statuses</option>
                    </select>
                    <button className="btn-primary" onClick={async () => {
                      const rList = await hostelInventoryAPI.getRooms({ status: 'AVAILABLE' });
                      setRooms(rList.results || rList);
                      setShowModal('allocation');
                    }}>New Allocation</button>
                  </div>
                </div>
                <div className="card-body">
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Student</th>
                          <th>Username</th>
                          <th>Hostel / Room</th>
                          <th>Allocated Date</th>
                          <th>Security Deposit</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allocations.map(a => (
                          <tr key={a.id}>
                            <td><strong>{a.resident_name}</strong></td>
                            <td>{a.resident_username}</td>
                            <td>{a.hostel_name} — Room {a.room_number}</td>
                            <td>{a.allocation_date}</td>
                            <td>₹{a.security_deposit}</td>
                            <td>
                              <span className={`badge ${a.status === 'ACTIVE' ? 'badge-green' : 'badge-gray'}`}>
                                {a.status_display}
                              </span>
                            </td>
                            <td>
                              {a.status === 'ACTIVE' ? (
                                <button 
                                  className="btn-sm btn-danger" 
                                  onClick={async () => {
                                    if (await confirmDialog(`Are you sure you want to vacate ${a.resident_name}?`)) {
                                      try {
                                        await hostelInventoryAPI.updateAllocation(a.id, { status: 'VACATED' });
                                        fetchData();
                                      } catch (err) {
                                        setError('Failed to vacate allocation');
                                      }
                                    }
                                  }}
                                >
                                  Vacate
                                </button>
                              ) : (
                                <span style={{ fontSize: '12px', color: '#6b7280' }}>-</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* ATTENDANCE TAB */}
            {isStaff && activeTab === 'attendance' && (
              <div className="card">
                <div className="card-header">
                  <h2>Daily Hostel Attendance Roll Call</h2>
                  <div className="card-header-actions">
                    <input type="date" className="search-input" value={attendanceDate} onChange={(e) => setAttendanceDate(e.target.value)} />
                  </div>
                </div>
                <div className="card-body">
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Resident Student</th>
                          <th>Room</th>
                          <th>Status</th>
                          <th>Late Entry</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {attendance.map(a => (
                          <tr key={a.id}>
                            <td><strong>{a.resident_name}</strong></td>
                            <td>Room {a.room_number}</td>
                            <td>
                              <span className={`badge ${a.is_present ? 'badge-green' : 'badge-red'}`}>
                                {a.is_present ? 'Present' : 'Absent'}
                              </span>
                            </td>
                            <td>{a.is_late_entry ? '⚠️ Late Entry' : 'On Time'}</td>
                            <td>
                              <button className="btn-sm btn-outline" onClick={() => handleMarkPresent(a.resident, a.room, !a.is_present)}>
                                Toggle Status
                              </button>
                            </td>
                          </tr>
                        ))}
                        {attendance.length === 0 && (
                          <tr>
                            <td colSpan="5" style={{ textAlign: 'center', color: '#6b7280' }}>
                              No attendance logged for {attendanceDate}. Mark attendance from room allocations.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* VISITORS TAB */}
            {isStaff && activeTab === 'visitors' && (
              <div className="card">
                <div className="card-header">
                  <h2>Hostel Visitor Logs</h2>
                  <div className="card-header-actions">
                    <input type="date" className="search-input" value={visitorFilterDate} onChange={(e) => setVisitorFilterDate(e.target.value)} />
                    <button className="btn-primary" onClick={() => setShowModal('visitor')}>+ Record Visitor</button>
                  </div>
                </div>
                <div className="card-body">
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Visitor Name</th>
                          <th>Student Visited</th>
                          <th>Relationship</th>
                          <th>Purpose</th>
                          <th>Entry Time</th>
                          <th>Exit Time</th>
                          <th>Pass Number</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visitors.map(v => (
                          <tr key={v.id}>
                            <td><strong>{v.visitor_name}</strong><br/><span style={{ fontSize: '12px', color: '#6b7280' }}>{v.visitor_mobile}</span></td>
                            <td>{v.student_name}</td>
                            <td>{v.relationship}</td>
                            <td>{v.purpose}</td>
                            <td>{new Date(v.entry_time).toLocaleString()}</td>
                            <td>{v.exit_time ? new Date(v.exit_time).toLocaleString() : 'Active'}</td>
                            <td><code>{v.pass_number}</code></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* OUTPASS & LEAVES TAB */}
            {isStaff && activeTab === 'leaves' && (
              <div className="card">
                <div className="card-header">
                  <h2>Outpass & Leaves Management</h2>
                </div>
                <div className="card-body">
                  <div className="sub-tabs" style={{ display: 'flex', gap: '10px', marginBottom: '20px', borderBottom: '1px solid #e5e7eb', paddingBottom: '10px' }}>
                    <button 
                      className={`btn-tab ${activeLeaveSubTab === 'outpasses' ? 'active' : ''}`}
                      style={{
                        padding: '8px 16px',
                        border: 'none',
                        background: activeLeaveSubTab === 'outpasses' ? '#2563eb' : 'transparent',
                        color: activeLeaveSubTab === 'outpasses' ? '#fff' : '#4b5563',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        transition: 'all 0.2s'
                      }}
                      onClick={() => setActiveLeaveSubTab('outpasses')}
                    >
                      Outpass Tickets
                    </button>
                    <button 
                      className={`btn-tab ${activeLeaveSubTab === 'leaves' ? 'active' : ''}`}
                      style={{
                        padding: '8px 16px',
                        border: 'none',
                        background: activeLeaveSubTab === 'leaves' ? '#2563eb' : 'transparent',
                        color: activeLeaveSubTab === 'leaves' ? '#fff' : '#4b5563',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        transition: 'all 0.2s'
                      }}
                      onClick={() => setActiveLeaveSubTab('leaves')}
                    >
                      Leave Requests
                    </button>
                  </div>

                  {activeLeaveSubTab === 'outpasses' ? (
                    <div>
                      <div className="filters-row" style={{ display: 'flex', gap: '15px', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                          <input
                            type="text"
                            placeholder="Search resident/ticket..."
                            value={outpassSearchQuery}
                            onChange={(e) => setOutpassSearchQuery(e.target.value)}
                            className="search-input"
                            style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', minWidth: '220px' }}
                          />
                          <select 
                            className="filter-select" 
                            value={outpassFilterStatus}
                            onChange={(e) => setOutpassFilterStatus(e.target.value)}
                            style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px' }}
                          >
                            <option value="ACTIVE">Active Outpasses</option>
                            <option value="CLOSED">Closed Outpasses</option>
                          </select>
                        </div>
                        <button className="btn btn-primary" onClick={() => setShowModal('outpass')}>
                          + Issue Outpass
                        </button>
                      </div>

                      <div className="table-container">
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>Ticket No</th>
                              <th>Resident</th>
                              <th>Out Date & Time</th>
                              <th>Expected Return</th>
                              <th>Actual Return</th>
                              <th>Issued By</th>
                              <th>Status</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {outpasses.length === 0 ? (
                              <tr>
                                <td colSpan="8" style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                                  No outpass records found matching the filters.
                                </td>
                              </tr>
                            ) : (
                              outpasses.map(o => (
                                <tr key={o.id}>
                                  <td><code>{o.outpass_number}</code></td>
                                  <td><strong>{o.student_name}</strong><br/><code>{o.student_username}</code></td>
                                  <td>{o.out_date} at {o.out_time}</td>
                                  <td>{o.expected_return_date} at {o.expected_return_time}</td>
                                  <td>{o.actual_return_time ? new Date(o.actual_return_time).toLocaleString() : <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Not returned yet</span>}</td>
                                  <td>{o.issued_by_name || 'System'}</td>
                                  <td>
                                    <span className={`badge ${o.status === 'ACTIVE' ? 'badge-green' : 'badge-gray'}`}>
                                      {o.status_display}
                                    </span>
                                  </td>
                                  <td>
                                    {o.status === 'ACTIVE' && (
                                      <button className="btn-sm btn-success" onClick={() => handleCloseOutpass(o.id)}>
                                        Mark Returned
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="filters-row" style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
                        <select className="filter-select" onChange={(e) => setLeaveStatusFilter(e.target.value)}>
                          <option value="">All Requests</option>
                          <option value="PENDING">Pending</option>
                          <option value="APPROVED">Approved</option>
                          <option value="REJECTED">Rejected</option>
                        </select>
                      </div>

                      <div className="table-container">
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>Student</th>
                              <th>Leave Type</th>
                              <th>Duration</th>
                              <th>Destination / Purpose</th>
                              <th>Gatepass</th>
                              <th>Status</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {leaves.length === 0 ? (
                              <tr>
                                <td colSpan="7" style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                                  No leave requests found matching the filters.
                                </td>
                              </tr>
                            ) : (
                              leaves.map(l => (
                                <tr key={l.id}>
                                  <td><strong>{l.student_name}</strong><br/><code>{l.student_username}</code></td>
                                  <td>{l.leave_type_display}</td>
                                  <td>{l.from_date} to {l.to_date}</td>
                                  <td><strong>{l.destination}</strong><br/><span style={{ fontSize: '12px', color: '#6b7280' }}>{l.reason}</span></td>
                                  <td><code>{l.gate_pass_number || 'NA'}</code></td>
                                  <td>
                                    <span className={`badge ${l.status === 'PENDING' ? 'badge-orange' : l.status === 'APPROVED' ? 'badge-green' : 'badge-red'}`}>
                                      {l.status_display}
                                    </span>
                                  </td>
                                  <td>
                                    {l.status === 'PENDING' && (
                                      <div className="action-buttons">
                                        <button className="btn-sm btn-success" onClick={() => handleApproveLeave(l.id, 'APPROVE')}>Approve</button>
                                        <button className="btn-sm btn-danger" onClick={() => handleApproveLeave(l.id, 'REJECT')}>Reject</button>
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* COMPLAINTS TAB */}
            {isStaff && activeTab === 'complaints' && (
              <div className="card">
                <div className="card-header">
                  <h2>Hostel Maintenance complaints log</h2>
                  <div className="card-header-actions">
                    <select className="filter-select" onChange={(e) => setComplaintStatusFilter(e.target.value)} value={complaintStatusFilter}>
                      <option value="">All Statuses</option>
                      <option value="SUBMITTED">Submitted</option>
                      <option value="UNDER_VERIFICATION">Under Verification</option>
                      <option value="CONFIRMED_DAMAGED">Confirmed Damaged</option>
                      <option value="UNDER_REPAIR">Under Repair</option>
                      <option value="AWAITING_REPLACEMENT">Awaiting Replacement</option>
                      <option value="REPLACEMENT_ORDERED">Replacement Ordered</option>
                      <option value="DELIVERED">Delivered</option>
                      <option value="INSTALLED">Installed</option>
                      <option value="RESOLVED">Resolved</option>
                      <option value="OPEN">Open (Legacy)</option>
                      <option value="ASSIGNED">Assigned (Legacy)</option>
                      <option value="IN_PROGRESS">In Progress (Legacy)</option>
                    </select>
                  </div>
                </div>
                <div className="card-body">
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Room</th>
                          <th>Reported By</th>
                          <th>Category</th>
                          <th>Accessory / Asset</th>
                          <th>Description</th>
                          <th>Priority</th>
                          <th>Status</th>
                          <th>Workflow Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {complaints.length === 0 ? (
                          <tr>
                            <td colSpan="8" style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                              No complaints found matching the status filter.
                            </td>
                          </tr>
                        ) : (
                          complaints.map(c => (
                            <tr key={c.id}>
                              <td><strong>Room {c.room_number}</strong><br/><span style={{ fontSize: '11px', color: '#6b7280' }}>{c.hostel_name}</span></td>
                              <td>{c.reported_by_name}</td>
                              <td>{c.category_display}</td>
                              <td>
                                {c.asset_label ? (
                                  <div>
                                    <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#4f46e5' }}>{c.asset_label}</span>
                                    <br/>
                                    <span style={{ fontSize: '10px', background: '#e0e7ff', color: '#4338ca', padding: '2px 6px', borderRadius: '4px', textTransform: 'uppercase' }}>
                                      {c.asset_type_display || c.asset_type}
                                    </span>
                                  </div>
                                ) : c.asset_other_description ? (
                                  <span style={{ fontSize: '12px', color: '#dc2626', fontStyle: 'italic' }}>
                                    Other: {c.asset_other_description}
                                  </span>
                                ) : (
                                  <span style={{ fontSize: '11px', color: '#9ca3af' }}>General Issue</span>
                                )}
                              </td>
                              <td>
                                <div style={{ maxWidth: '250px', whiteSpace: 'normal', fontSize: '13px' }}>
                                  <strong>{c.title}</strong>
                                  <p style={{ margin: '4px 0 0', color: '#4b5563' }}>{c.description}</p>
                                </div>
                              </td>
                              <td>
                                <span className={`badge ${c.priority === 'HIGH' ? 'badge-red' : c.priority === 'MEDIUM' ? 'badge-orange' : 'badge-blue'}`}>
                                  {c.priority_display}
                                </span>
                              </td>
                              <td>
                                <span className={`badge`} style={{
                                  background: c.status === 'SUBMITTED' ? '#f97316' :
                                              c.status === 'UNDER_VERIFICATION' ? '#3b82f6' :
                                              c.status === 'CONFIRMED_DAMAGED' ? '#ef4444' :
                                              c.status === 'UNDER_REPAIR' ? '#8b5cf6' :
                                              c.status === 'AWAITING_REPLACEMENT' ? '#f59e0b' :
                                              c.status === 'REPLACEMENT_ORDERED' ? '#0ea5e9' :
                                              c.status === 'DELIVERED' ? '#06b6d4' :
                                              c.status === 'INSTALLED' ? '#10b981' :
                                              c.status === 'RESOLVED' ? '#22c55e' : '#6b7280',
                                  color: '#fff',
                                  textTransform: 'uppercase',
                                  fontWeight: 'bold'
                                }}>
                                  {c.status_display || c.status}
                                </span>
                              </td>
                              <td>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', minWidth: '130px' }}>
                                  {/* Verification stage */}
                                  {c.status === 'SUBMITTED' && (
                                    <button className="btn-sm btn-info" style={{ width: '100%' }} onClick={() => hostelInventoryAPI.updateComplaint(c.id, { status: 'UNDER_VERIFICATION' }).then(() => { setSuccess('Moved to verification'); fetchData(); })}>
                                      🔎 Start Verification
                                    </button>
                                  )}
                                  
                                  {/* Damage assessment stage */}
                                  {c.status === 'UNDER_VERIFICATION' && (
                                    <button className="btn-sm btn-danger" style={{ width: '100%' }} onClick={() => handleMarkComplaintDamaged(c.id)}>
                                      ⚠️ Confirm Damaged
                                    </button>
                                  )}

                                  {/* Repair or Replacement action stage */}
                                  {(c.status === 'CONFIRMED_DAMAGED' || c.status === 'UNDER_VERIFICATION') && (
                                    <button className="btn-sm btn-warning" style={{ width: '100%' }} onClick={() => {
                                      setSelectedComplaint(c);
                                      setRepairForm({ repair_started_date: new Date().toISOString().split('T')[0], repair_remarks: '' });
                                      setShowModal('startRepair');
                                    }}>
                                      🔧 Repair Asset
                                    </button>
                                  )}

                                  {/* Replacement generation stage */}
                                  {(c.status === 'CONFIRMED_DAMAGED' || c.status === 'UNDER_REPAIR') && (
                                    <button className="btn-sm btn-primary" style={{ width: '100%' }} onClick={() => {
                                      setSelectedComplaint(c);
                                      setReplacementForm({
                                        accessory_type: c.asset_type || 'OTHER',
                                        quantity: 1,
                                        reason: `Replacement request for Room ${c.room_number}`,
                                        remarks: '',
                                        priority: c.priority
                                      });
                                      setShowModal('requestReplacement');
                                    }}>
                                      📦 Req Replacement
                                    </button>
                                  )}

                                  {/* Repair completion stage */}
                                  {c.status === 'UNDER_REPAIR' && (
                                    <button className="btn-sm btn-success" style={{ width: '100%' }} onClick={() => {
                                      setSelectedComplaint(c);
                                      setRepairCompleteForm({
                                        repair_completed_date: new Date().toISOString().split('T')[0],
                                        repair_remarks: '',
                                        resolution_notes: 'Repaired successfully.'
                                      });
                                      setShowModal('completeRepair');
                                    }}>
                                      ✅ Complete Repair
                                    </button>
                                  )}

                                  {/* Tracking details */}
                                  {c.status === 'AWAITING_REPLACEMENT' && (
                                    <span style={{ fontSize: '11px', color: '#b45309', fontStyle: 'italic', fontWeight: 'bold' }}>
                                      ⏳ Awaiting PR Approval
                                    </span>
                                  )}
                                  {c.status === 'REPLACEMENT_ORDERED' && (
                                    <span style={{ fontSize: '11px', color: '#0369a1', fontStyle: 'italic', fontWeight: 'bold' }}>
                                      📦 Order Placed by Finance
                                    </span>
                                  )}
                                  {c.status === 'DELIVERED' && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                      <span style={{ fontSize: '11px', color: '#0f766e', fontStyle: 'italic', fontWeight: 'bold' }}>
                                        🚚 Item Delivered
                                      </span>
                                      <span style={{ fontSize: '9px', color: '#6b7280' }}>
                                        Install via Purchases Tab
                                      </span>
                                    </div>
                                  )}

                                  {/* Resolve (Any status not yet completed) */}
                                  {c.status !== 'RESOLVED' && c.status !== 'CLOSED' && c.status !== 'INSTALLED' && (
                                    <button className="btn-sm btn-outline" style={{ width: '100%', marginTop: '4px' }} onClick={() => {
                                      const notes = prompt('Enter resolution notes:');
                                      if (notes !== null) handleResolveComplaint(c.id, notes);
                                    }}>
                                      Resolve Directly
                                    </button>
                                  )}
                                  {(c.status === 'RESOLVED' || c.status === 'CLOSED' || c.status === 'INSTALLED') && (
                                    <span style={{ color: '#16a34a', fontWeight: 'bold', fontSize: '12px' }}>
                                      ✅ Completed
                                    </span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* FEES TAB */}
            {isStaff && activeTab === 'fees' && (
              <div className="card">
                <div className="card-header">
                  <h2>Hostel Fee Ledger</h2>
                  <div className="card-header-actions">
                    <select className="filter-select" onChange={(e) => setFeeStatusFilter(e.target.value)}>
                      <option value="">All Statuses</option>
                      <option value="PENDING">Pending</option>
                      <option value="PAID">Paid</option>
                    </select>
                    <button className="btn-primary" onClick={() => setShowModal('fee')}>+ Bill Student</button>
                  </div>
                </div>
                <div className="card-body">
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Resident</th>
                          <th>Fee Type</th>
                          <th>Amount</th>
                          <th>Due Date</th>
                          <th>Status</th>
                          <th>Receipt Info</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fees.map(f => (
                          <tr key={f.id}>
                            <td><strong>{f.resident_name}</strong><br/>Room {f.room_number}</td>
                            <td>{f.fee_type_display} ({f.month_year})</td>
                            <td><strong>₹{f.amount}</strong></td>
                            <td>{f.due_date}</td>
                            <td>
                              <span className={`badge ${f.status === 'PAID' ? 'badge-green' : 'badge-orange'}`}>
                                {f.status_display}
                              </span>
                            </td>
                            <td>{f.receipt_number ? `${f.receipt_number} (${f.payment_mode})` : 'NA'}</td>
                            <td>
                              {f.status === 'PENDING' && (
                                <button className="btn-sm btn-success" onClick={() => {
                                  const num = prompt('Enter Receipt/Transaction ID:');
                                  if (num) handleRecordFeePayment(f.id, f.amount, 'UPI', num);
                                }}>Collect Payment</button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* SETTINGS TAB */}
            {isStaff && activeTab === 'settings' && settings && (
              <div className="card">
                <div className="card-header">
                  <h2>Configure Hostel Settings</h2>
                </div>
                <div className="card-body">
                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    try {
                      await hostelInventoryAPI.updateSettings(settings);
                      setSuccess('Settings updated successfully');
                    } catch (err) {
                      setError('Failed to save settings');
                    }
                  }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                      <div>
                        <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px' }}>Late Entry Cutoff Time</label>
                        <input type="text" className="search-input" style={{ width: '100%' }} value={settings.late_entry_time} onChange={(e) => setSettings({ ...settings, late_entry_time: e.target.value })} />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px' }}>Night Curfew Time</label>
                        <input type="text" className="search-input" style={{ width: '100%' }} value={settings.night_curfew_time} onChange={(e) => setSettings({ ...settings, night_curfew_time: e.target.value })} />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px' }}>Late Entry Fine Amount (₹)</label>
                        <input type="number" className="search-input" style={{ width: '100%' }} value={settings.fine_per_late_entry} onChange={(e) => setSettings({ ...settings, fine_per_late_entry: e.target.value })} />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px' }}>Curfew Auto-Fine Trigger</label>
                        <select className="filter-select" style={{ width: '100%' }} value={settings.auto_fine_enabled} onChange={(e) => setSettings({ ...settings, auto_fine_enabled: e.target.value === 'true' })}>
                          <option value="false">Manual Fine Review</option>
                          <option value="true">Enable Auto-Fine On Late Logs</option>
                        </select>
                      </div>
                    </div>
                    <button type="submit" className="btn-primary">Save Config</button>
                  </form>
                </div>
              </div>
            )}

            {/* HOSTEL INVENTORY */}
            {isStaff && activeTab === 'assets' && (
              <div className="card">
                <div className="card-header">
                  <h2>Hostel Inventory</h2>
                  <div className="card-header-actions">
                    <input type="text" className="search-input" placeholder="Search label/number..." value={itemSearch} onChange={(e) => setItemSearch(e.target.value)} />
                    <select className="filter-select" value={roomFilter.hostel} onChange={(e) => setRoomFilter({ ...roomFilter, hostel: e.target.value })}>
                      <option value="">All Hostels</option>
                      {hostels.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
                    </select>
                    <select className="filter-select" value={itemFilter.category} onChange={(e) => setItemFilter({ ...itemFilter, category: e.target.value })}>
                      <option value="">All Accessory Types</option>
                      <option value="BED">Bed</option>
                      <option value="MATTRESS">Mattress</option>
                      <option value="FAN">Fan</option>
                      <option value="LIGHT">Light/Tube</option>
                      <option value="TABLE">Study Table</option>
                      <option value="CHAIR">Study Chair</option>
                      <option value="CUPBOARD">Cupboard/Wardrobe</option>
                      <option value="AC">AC Unit</option>
                      <option value="BUCKET">Bucket</option>
                      <option value="MIRROR">Mirror</option>
                      <option value="CURTAIN">Curtain</option>
                      <option value="DUSTBIN">Dustbin</option>
                      <option value="GEYSER">Geyser</option>
                      <option value="STUDY_LAMP">Study Lamp</option>
                      <option value="SWITCHBOARD">Switch Board</option>
                      <option value="OTHER">Other</option>
                    </select>
                  </div>
                </div>
                <div className="card-body">
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Asset Label</th>
                          <th>Asset Number</th>
                          <th>Accessory Type</th>
                          <th>Purchase Date</th>
                          <th>Current Status</th>
                          <th>Availability Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.length === 0 ? (
                          <tr>
                            <td colSpan="6" style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                              No available assets found in hostel inventory.
                            </td>
                          </tr>
                        ) : (
                          items.map(asset => (
                            <tr key={asset.id}>
                              <td><strong style={{ fontFamily: 'monospace' }}>{asset.asset_label}</strong></td>
                              <td><code>{asset.asset_number || 'N/A'}</code></td>
                              <td><span style={{ fontWeight: 'bold', color: '#4f46e5' }}>{asset.asset_type_display || asset.asset_type}</span></td>
                              <td>{asset.purchase_date || 'N/A'}</td>
                              <td>
                                <span className={`badge ${
                                  asset.status === 'WORKING' ? 'badge-green' : 
                                  asset.status === 'DAMAGED' ? 'badge-red' : 
                                  asset.status === 'UNDER_REPAIR' ? 'badge-orange' : 'badge-blue'
                                }`}>
                                  {asset.status_display || asset.status}
                                </span>
                              </td>
                              <td>
                                <span className="badge badge-green">
                                  {asset.availability_status}
                                </span>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* PROCUREMENT AND PO TAB */}
            {isStaff && activeTab === 'purchases' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>



                <div className="card">
                  <div className="card-header">
                    <h2>Hostel Accessories Procurement</h2>
                    <button className="btn-primary" onClick={() => {
                      setHostelPrForm({
                        hostel: '',
                        accessory_type: 'BED',
                        quantity: 1,
                        priority: 'MEDIUM',
                        reason: '',
                        remarks: ''
                      });
                      setShowModal('hostelPr');
                    }}>
                      + Request Hostel Accessories
                    </button>
                  </div>
                  <div className="card-body">
                    <div className="table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Request ID</th>
                            <th>Hostel & Room</th>
                            <th>Accessory Type</th>
                            <th>Qty</th>
                            <th>Justification & Priority</th>
                            <th>Status</th>
                            <th>Cost & Vendor Details</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {hostelPurchaseRequests.length === 0 ? (
                            <tr>
                              <td colSpan="8" style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                                No hostel accessory procurement requests found.
                              </td>
                            </tr>
                          ) : (
                            hostelPurchaseRequests.map(hpr => (
                              <tr key={hpr.id}>
                                <td><strong>HPR-{hpr.id}</strong>{hpr.complaint && <span style={{ fontSize: '10px', color: '#6b7280' }}><br/>Complaint #{hpr.complaint}</span>}</td>
                                <td><strong>{hpr.room_number ? `Room ${hpr.room_number}` : 'Bulk Stock'}</strong><br/><span style={{ fontSize: '11px', color: '#6b7280' }}>{hpr.hostel_name}</span></td>
                                <td>
                                  <span style={{ fontWeight: 'bold', color: '#4f46e5' }}>{hpr.accessory_type_display}</span>
                                  {hpr.existing_asset_label && <div style={{ fontSize: '10px', color: '#dc2626' }}>Replacing: {hpr.existing_asset_label}</div>}
                                </td>
                                <td>{hpr.quantity}</td>
                                <td>
                                  <div style={{ maxWidth: '200px', whiteSpace: 'normal', fontSize: '12px' }}>
                                    <p style={{ margin: 0, fontStyle: 'italic' }}>"{hpr.reason}"</p>
                                    <span className={`badge ${hpr.priority === 'HIGH' ? 'badge-red' : hpr.priority === 'MEDIUM' ? 'badge-orange' : 'badge-blue'}`} style={{ marginTop: '4px' }}>
                                      {hpr.priority_display}
                                    </span>
                                  </div>
                                </td>
                                <td>
                                  <span className="badge" style={{
                                    background: (hpr.status === 'REQUESTED' || hpr.status === 'PENDING') ? '#f59e0b' :
                                                hpr.status === 'APPROVED_BY_ADMIN' ? '#3b82f6' :
                                                hpr.status === 'PURCHASED' ? '#10b981' :
                                                hpr.status === 'INSTALLED' ? '#047857' : '#ef4444',
                                    color: '#fff',
                                    fontWeight: 'bold',
                                    textTransform: 'uppercase'
                                  }}>
                                    {hpr.status_display}
                                  </span>
                                </td>
                                <td>
                                  {hpr.status === 'PURCHASED' || hpr.status === 'INSTALLED' ? (
                                    <div style={{ fontSize: '12px' }}>
                                      <strong>₹{hpr.purchase_cost || '0'}</strong> via {hpr.vendor_name || 'N/A'}
                                      <br/>
                                      <span style={{ fontSize: '10px', color: '#6b7280' }}>Inv: {hpr.invoice_number || 'N/A'}</span>
                                      {hpr.new_asset_label && <div style={{ fontSize: '10px', fontWeight: 'bold', color: '#16a34a' }}>New Label: {hpr.new_asset_label}</div>}
                                    </div>
                                  ) : (
                                    <span style={{ fontSize: '11px', color: '#9ca3af', fontStyle: 'italic' }}>Pending Purchase</span>
                                  )}
                                </td>
                                <td>
                                  <div style={{ display: 'flex', gap: '5px' }}>
                                    {(hpr.status === 'REQUESTED' || hpr.status === 'PENDING') && (
                                      <>
                                        <button className="btn-sm btn-success" onClick={() => handleApproveHostelPR(hpr.id, 'APPROVED_BY_ADMIN')}>
                                          Approve
                                        </button>
                                        <button className="btn-sm btn-danger" onClick={() => {
                                          const reason = prompt('Enter rejection reason:');
                                          if (reason !== null) handleApproveHostelPR(hpr.id, 'REJECTED', reason);
                                        }}>
                                          Reject
                                        </button>
                                      </>
                                    )}
                                    {hpr.status === 'APPROVED_BY_ADMIN' && (
                                      <span style={{ fontSize: '11px', color: '#3b82f6', fontStyle: 'italic' }}>
                                        Awaiting Finance Purchase
                                      </span>
                                    )}

                                    {hpr.status === 'PURCHASED' && (
                                      <button className="btn-sm btn-primary" onClick={() => {
                                        setSelectedHpr(hpr);
                                        setInstallForm({
                                          new_asset_label: hpr.new_asset_label || `${hpr.room_number || 'BULK'}-${hpr.accessory_type}-01`,
                                          remarks: '',
                                          resolution_notes: `Installed new replacement ${hpr.accessory_type_display}.`
                                        });
                                        setShowModal('installAsset');
                                      }}>
                                        🔧 Install Asset
                                      </button>
                                    )}

                                    {hpr.status === 'INSTALLED' && (
                                      <span style={{ fontSize: '11px', color: '#16a34a', fontWeight: 'bold' }}>
                                        Installed & Verified
                                      </span>
                                    )}

                                    {hpr.status === 'REJECTED' && (
                                      <span style={{ fontSize: '11px', color: '#dc2626', fontStyle: 'italic' }}>
                                        Rejected: {hpr.rejection_reason || 'No reason'}
                                      </span>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* VENDORS TAB */}
            {isStaff && activeTab === 'vendors' && (
              <div className="card">
                <div className="card-header">
                  <h2>Vendor Directory</h2>
                  <button className="btn-primary" onClick={() => setShowModal('vendor')}>Register Vendor</button>
                </div>
                <div className="card-body">
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Vendor Name</th>
                          <th>Contact Person</th>
                          <th>Contact Details</th>
                          <th>GST Number</th>
                          <th>Rating</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {vendors.map(v => (
                          <tr key={v.id}>
                            <td><strong>{v.name}</strong></td>
                            <td>{v.contact_person}</td>
                            <td>{v.phone} / {v.email}</td>
                            <td><code>{v.gst_number || 'NA'}</code></td>
                            <td>⭐ {v.rating}</td>
                            <td>
                              <span className={`badge ${v.is_active ? 'badge-green' : 'badge-red'}`}>
                                {v.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* STOCK TRANSACTION TAB */}
            {isStaff && activeTab === 'stock' && (
              <div className="card">
                <div className="card-header">
                  <h2>Stock Movement Log</h2>
                  <button className="btn-primary" onClick={() => setShowModal('stock')}>+ Log Stock Transaction</button>
                </div>
                <div className="card-body">
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Item Name</th>
                          <th>Transaction Type</th>
                          <th>Quantity</th>
                          <th>Location Flow</th>
                          <th>Logged By</th>
                          <th>Transaction Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stockTransactions.map(st => (
                          <tr key={st.id}>
                            <td><strong>{st.item_name}</strong><br/><code>{st.item_code}</code></td>
                            <td>
                              <span className={`badge ${st.transaction_type === 'IN' ? 'badge-green' : 'badge-orange'}`}>
                                {st.transaction_type_display}
                              </span>
                            </td>
                            <td><strong>{st.quantity} units</strong></td>
                            <td>{st.from_location || 'Vendor'} → {st.to_location || 'Storage'}</td>
                            <td>{st.performed_by_name}</td>
                            <td>{new Date(st.transaction_date).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* STUDENT ROOM PROFILE */}
            {isStudent && activeTab === 'student-overview' && studentProfile && (
              <div className="grid-2-col">
                {studentProfile.allocated ? (
                  <>
                    <div className="card">
                      <div className="card-header">
                        <h2>My Hostel Allocation</h2>
                      </div>
                      <div className="card-body">
                        <div style={{ display: 'grid', gap: '12px' }}>
                          <p><strong>Hostel Name:</strong> {studentProfile.allocation.hostel_name}</p>
                          <p><strong>Building & Floor:</strong> {studentProfile.allocation.building_name}</p>
                          <p><strong>Room Number:</strong> Room {studentProfile.allocation.room_number}</p>
                          <p><strong>Rent Amount:</strong> ₹{studentProfile.allocation.monthly_rent || '0.00'} / month</p>
                          <p><strong>Allocation Date:</strong> {studentProfile.allocation.allocation_date}</p>
                        </div>
                      </div>
                    </div>

                    <div className="card">
                      <div className="card-header">
                        <h2>Roommates Directory</h2>
                      </div>
                      <div className="card-body">
                        {studentProfile.roommates.map(r => (
                          <div key={r.id} className="list-item">
                            <div className="list-item-content">
                              <h4>{r.name}</h4>
                              <p>Roll Number / Username: <code>{r.username}</code></p>
                            </div>
                          </div>
                        ))}
                        {studentProfile.roommates.length === 0 && <p style={{ color: '#6b7280' }}>No roommates in this room.</p>}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="card" style={{ gridColumn: 'span 2', textAlign: 'center', padding: '40px' }}>
                    <h2>No Room Allocated</h2>
                    <p style={{ color: '#6b7280' }}>You do not have any active room allocations. Contact the Hostel Administration office to get a room.</p>
                  </div>
                )}
              </div>
            )}

            {/* STUDENT LEAVES */}
            {isStudent && activeTab === 'student-leaves' && (
              <div className="card">
                <div className="card-header">
                  <h2>My Leave Requests</h2>
                  <button className="btn-primary" onClick={() => setShowModal('leave')}>+ Apply for Leave</button>
                </div>
                <div className="card-body">
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Leave Type</th>
                          <th>Duration</th>
                          <th>Destination</th>
                          <th>Gatepass Code</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {leaves.map(l => (
                          <tr key={l.id}>
                            <td><strong>{l.leave_type_display}</strong></td>
                            <td>{l.from_date} to {l.to_date}</td>
                            <td>{l.destination}</td>
                            <td><code>{l.gate_pass_number || 'PENDING'}</code></td>
                            <td>
                              <span className={`badge ${l.status === 'PENDING' ? 'badge-orange' : l.status === 'APPROVED' ? 'badge-green' : 'badge-red'}`}>
                                {l.status_display}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* STUDENT COMPLAINTS */}
            {isStudent && activeTab === 'student-complaints' && (
              <div className="card">
                <div className="card-header">
                  <h2>My Complaint Tickets</h2>
                  <button className="btn-primary" onClick={() => setShowModal('complaint')}>+ File New Complaint</button>
                </div>
                <div className="card-body">
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Category</th>
                          <th>Description</th>
                          <th>Date Submitted</th>
                          <th>Priority</th>
                          <th>Status</th>
                          <th>Resolution Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {complaints.map(c => (
                          <tr key={c.id}>
                            <td><strong>{c.category_display}</strong></td>
                            <td>{c.description}</td>
                            <td>{new Date(c.created_at).toLocaleDateString()}</td>
                            <td>
                              <span className={`badge ${c.priority === 'HIGH' ? 'badge-red' : 'badge-orange'}`}>
                                {c.priority_display}
                              </span>
                            </td>
                            <td>
                              <span className={`badge ${c.status === 'RESOLVED' ? 'badge-green' : 'badge-red'}`}>
                                {c.status_display}
                              </span>
                            </td>
                            <td>{c.resolution_notes || 'Pending assignment.'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* STUDENT FEES */}
            {isStudent && activeTab === 'student-fees' && (
              <div className="card">
                <div className="card-header">
                  <h2>My Fee Invoices & Payments</h2>
                </div>
                <div className="card-body">
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Bill Type</th>
                          <th>Period</th>
                          <th>Amount</th>
                          <th>Due Date</th>
                          <th>Payment Status</th>
                          <th>Receipt Info</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fees.map(f => (
                          <tr key={f.id}>
                            <td><strong>{f.fee_type_display}</strong></td>
                            <td>{f.month_year}</td>
                            <td><strong>₹{f.amount}</strong></td>
                            <td>{f.due_date}</td>
                            <td>
                              <span className={`badge ${f.status === 'PAID' ? 'badge-green' : 'badge-orange'}`}>
                                {f.status_display}
                              </span>
                            </td>
                            <td>{f.receipt_number ? `${f.receipt_number} (${f.payment_mode})` : 'NA'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* STUDENT ASSETS */}
            {isStudent && activeTab === 'student-assets' && (
              <div className="card">
                <div className="card-header">
                  <h2>My Borrowed Assets</h2>
                </div>
                <div className="card-body">
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Item Name</th>
                          <th>Asset Code</th>
                          <th>Date Assigned</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {assignments.map(a => (
                          <tr key={a.id}>
                            <td><strong>{a.item_name}</strong></td>
                            <td><code>{a.item_code}</code></td>
                            <td>{a.assigned_date}</td>
                            <td>
                              <span className={`badge ${a.status === 'ACTIVE' ? 'badge-green' : 'badge-gray'}`}>
                                {a.status_display}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'calendar' && (
              <div className="card" style={{ padding: 0, overflow: 'visible' }}>
                <Calendar />
              </div>
            )}

          </div>
        )}

        {/* --- MODALS DIALOGS BACKDROP --- */}
        {showModal && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '20px', overflowY: 'auto'
          }}>
            <div className="card" style={{ width: '100%', maxWidth: showModal === 'roomDetail' ? '900px' : '600px', maxHeight: '90vh', overflowY: 'auto' }}>
              <div className="card-header">
                <h2>
                  {showModal === 'roomDetail'
                    ? `Room Full Details — Room ${selectedRoomDetails?.room?.room_number || ''}`
                    : showModal === 'outpass'
                    ? 'Issue Outpass Ticket'
                    : showModal === 'startRepair'
                    ? 'Start Repair Process'
                    : showModal === 'completeRepair'
                    ? 'Complete Repair & Resolve'
                    : showModal === 'requestReplacement'
                    ? 'Request Procurement Replacement'
                    : showModal === 'installAsset'
                    ? 'Install Purchased Replacement Asset'
                    : showModal === 'swapAsset'
                    ? 'Swap / Replace Room Asset'
                    : showModal === 'hostelPr'
                    ? 'Request Hostel Accessories Procurement'
                    : `Record New ${showModal.toUpperCase()}`}
                </h2>
                <button className="btn-outline" onClick={() => setShowModal(null)}>Close</button>
              </div>
              <div className="card-body">

                {/* Room Detail Modal Content */}
                {showModal === 'roomDetail' && selectedRoomDetails && (
                  <div>
                    {/* Room Quick Stats + Edit Button */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
                      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', flex: 1 }}>
                        <div style={{ flex: 1, minWidth: '140px', background: '#f3f4f6', padding: '10px', borderRadius: '6px' }}>
                          <span style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase', fontWeight: 'bold' }}>Hostel & Type</span>
                          <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#1f2937' }}>
                            {selectedRoomDetails.room.hostel_name} ({selectedRoomDetails.room.hostel_type})
                          </div>
                        </div>
                        <div style={{ flex: 1, minWidth: '140px', background: '#f3f4f6', padding: '10px', borderRadius: '6px' }}>
                          <span style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase', fontWeight: 'bold' }}>Location</span>
                          <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#1f2937' }}>
                            {selectedRoomDetails.room.building_name} — Floor {selectedRoomDetails.room.floor_name || selectedRoomDetails.room.floor_number}
                          </div>
                        </div>
                        <div style={{ flex: 1, minWidth: '140px', background: '#f3f4f6', padding: '10px', borderRadius: '6px' }}>
                          <span style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase', fontWeight: 'bold' }}>Occupancy</span>
                          <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#1f2937' }}>
                            {selectedRoomDetails.room.current_occupancy} / {selectedRoomDetails.room.capacity} occupied
                          </div>
                        </div>
                        <div style={{ flex: 1, minWidth: '140px', background: '#f3f4f6', padding: '10px', borderRadius: '6px' }}>
                          <span style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase', fontWeight: 'bold' }}>Rent & Status</span>
                          <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#1f2937' }}>
                            ₹{selectedRoomDetails.room.monthly_rent}/mo — {selectedRoomDetails.room.status}
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        className={isEditingRoom ? 'btn-outline' : 'btn-primary'}
                        style={{ padding: '6px 14px', fontSize: '12px', whiteSpace: 'nowrap' }}
                        onClick={() => {
                          if (isEditingRoom) {
                            setIsEditingRoom(false);
                          } else {
                            const r = selectedRoomDetails.room;
                            setEditRoomForm({
                              room_number: r.room_number || '',
                              room_type: r.room_type || 'DOUBLE',
                              capacity: r.capacity || '2',
                              monthly_rent: r.monthly_rent || '',
                              has_ac: r.has_ac || false,
                              has_wifi: r.has_wifi !== false,
                              has_attached_bath: r.has_attached_bath || false,
                              status: r.status || 'AVAILABLE',
                              notes: r.notes || '',
                            });
                            setIsEditingRoom(true);
                          }
                        }}
                      >
                        {isEditingRoom ? '✕ Cancel Edit' : '✏️ Edit Room Config'}
                      </button>
                    </div>

                    {/* Inline Edit Room Form */}
                    {isEditingRoom && (
                      <form onSubmit={handleEditRoomSubmit} style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
                        <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#1f2937', marginBottom: '14px' }}>Edit Room Configuration</h4>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>Room Number *</label>
                            <input type="text" className="search-input" style={{ padding: '6px 8px', fontSize: '13px' }} required value={editRoomForm.room_number} onChange={e => setEditRoomForm({ ...editRoomForm, room_number: e.target.value })} />
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>Room Type *</label>
                            <select className="filter-select" style={{ fontSize: '13px' }} value={editRoomForm.room_type} onChange={e => setEditRoomForm({ ...editRoomForm, room_type: e.target.value })}>
                              <option value="SINGLE">Single Room</option>
                              <option value="DOUBLE">Double Sharing</option>
                              <option value="TRIPLE">Triple Sharing</option>
                              <option value="DORMITORY">Dormitory Style</option>
                            </select>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>Capacity</label>
                            <input type="number" className="search-input" style={{ padding: '6px 8px', fontSize: '13px' }} min="1" value={editRoomForm.capacity} onChange={e => setEditRoomForm({ ...editRoomForm, capacity: e.target.value })} />
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>Monthly Rent (₹)</label>
                            <input type="number" className="search-input" style={{ padding: '6px 8px', fontSize: '13px' }} value={editRoomForm.monthly_rent} onChange={e => setEditRoomForm({ ...editRoomForm, monthly_rent: e.target.value })} />
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>Status</label>
                            <select className="filter-select" style={{ fontSize: '13px' }} value={editRoomForm.status} onChange={e => setEditRoomForm({ ...editRoomForm, status: e.target.value })}>
                              <option value="AVAILABLE">Available</option>
                              <option value="OCCUPIED">Occupied</option>
                              <option value="MAINTENANCE">Under Maintenance</option>
                            </select>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>Notes</label>
                            <input type="text" className="search-input" style={{ padding: '6px 8px', fontSize: '13px' }} value={editRoomForm.notes} onChange={e => setEditRoomForm({ ...editRoomForm, notes: e.target.value })} />
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '20px', marginTop: '12px' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: '600' }}>
                            <input type="checkbox" checked={editRoomForm.has_ac} onChange={e => setEditRoomForm({ ...editRoomForm, has_ac: e.target.checked })} /> Air Conditioner (AC)
                          </label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: '600' }}>
                            <input type="checkbox" checked={editRoomForm.has_wifi} onChange={e => setEditRoomForm({ ...editRoomForm, has_wifi: e.target.checked })} /> WiFi
                          </label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: '600' }}>
                            <input type="checkbox" checked={editRoomForm.has_attached_bath} onChange={e => setEditRoomForm({ ...editRoomForm, has_attached_bath: e.target.checked })} /> Attached Bath
                          </label>
                        </div>
                        <div style={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
                          <button type="submit" className="btn-primary" style={{ flex: 1 }}>Save Changes</button>
                          <button type="button" className="btn-outline" style={{ flex: 1 }} onClick={() => setIsEditingRoom(false)}>Cancel</button>
                        </div>
                      </form>
                    )}

                    {/* Inner Tabs */}
                    <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', gap: '15px', marginBottom: '15px' }}>
                      <button
                        type="button"
                        style={{
                          padding: '8px 16px',
                          border: 'none',
                          background: 'none',
                          borderBottom: roomDetailsTab === 'allocations' ? '2px solid #3b82f6' : 'none',
                          color: roomDetailsTab === 'allocations' ? '#2563eb' : '#4b5563',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                        onClick={() => setRoomDetailsTab('allocations')}
                      >
                        Residents & Bed Allocations
                      </button>
                      <button
                        type="button"
                        style={{
                          padding: '8px 16px',
                          border: 'none',
                          background: 'none',
                          borderBottom: roomDetailsTab === 'assets' ? '2px solid #3b82f6' : 'none',
                          color: roomDetailsTab === 'assets' ? '#2563eb' : '#4b5563',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                        onClick={() => setRoomDetailsTab('assets')}
                      >
                        Accessories & Asset Registry
                      </button>
                      <button
                        type="button"
                        style={{
                          padding: '8px 16px',
                          border: 'none',
                          background: 'none',
                          borderBottom: roomDetailsTab === 'events' ? '2px solid #3b82f6' : 'none',
                          color: roomDetailsTab === 'events' ? '#2563eb' : '#4b5563',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                        onClick={() => setRoomDetailsTab('events')}
                      >
                        Lifecycle Event Logs
                      </button>
                    </div>

                    {/* Tab 1: Allocations */}
                    {roomDetailsTab === 'allocations' && (
                      <div>
                        <h3 style={{ fontSize: '15px', fontWeight: 'bold', marginBottom: '10px', color: '#1f2937' }}>Current Occupants</h3>
                        {selectedRoomDetails.allocations.length === 0 ? (
                          <p style={{ fontStyle: 'italic', color: '#6b7280' }}>No active allocations in this room.</p>
                        ) : (
                          <div className="table-container">
                            <table className="data-table">
                              <thead>
                                <tr>
                                  <th>Student Name</th>
                                  <th>Username</th>
                                  <th>Role</th>
                                  <th>Allocated Bed Asset</th>
                                  <th>Allocation Date</th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectedRoomDetails.allocations.map(alloc => (
                                  <tr key={alloc.id}>
                                    <td><strong>{alloc.resident_name}</strong></td>
                                    <td>{alloc.resident_username}</td>
                                    <td>{alloc.resident_role}</td>
                                    <td>
                                      <span style={{ fontFamily: 'monospace', fontWeight: 'bold', background: '#dbeafe', color: '#1e40af', padding: '2px 6px', borderRadius: '4px' }}>
                                        {alloc.bed_label || 'None (Bed not assigned)'}
                                      </span>
                                    </td>
                                    <td>{alloc.allocation_date}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Tab 2: Accessories & Asset Registry */}
                    {roomDetailsTab === 'assets' && (
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                          <h3 style={{ fontSize: '15px', fontWeight: 'bold', color: '#1f2937' }}>Accessory Configurations & Unique Assets</h3>
                        </div>
                        
                        {/* Accessories quantities */}
                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '15px' }}>
                          {selectedRoomDetails.accessories.map(acc => (
                            <div key={acc.id} style={{ background: '#f3f4f6', border: '1px solid #e5e7eb', padding: '6px 12px', borderRadius: '20px', fontSize: '12px', color: '#374151' }}>
                              <strong>{acc.accessory_type}:</strong> {acc.quantity} units
                            </div>
                          ))}
                        </div>

                        {/* Individual Asset Registry table */}
                        <div className="table-container">
                          <table className="data-table">
                            <thead>
                              <tr>
                                <th>Asset Label</th>
                                <th>Accessory Type</th>
                                <th>Status</th>
                                <th>Occupied/Assigned</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedRoomDetails.assets.map(asset => (
                                <tr key={asset.id}>
                                  <td><strong style={{ fontFamily: 'monospace' }}>{asset.asset_label}</strong></td>
                                  <td>{asset.asset_type_display || asset.asset_type}</td>
                                  <td>
                                    <span className={`badge ${
                                      asset.status === 'WORKING' ? 'badge-green' : 
                                      asset.status === 'DAMAGED' ? 'badge-red' : 
                                      asset.status === 'UNDER_REPAIR' ? 'badge-orange' : 
                                      asset.status === 'REPLACED' ? 'badge-blue' : 'badge-red'
                                    }`}>
                                      {asset.status_display || asset.status}
                                    </span>
                                  </td>
                                  <td>
                                    <span style={{ fontSize: '11px', color: asset.is_occupied ? '#2563eb' : '#6b7280', fontWeight: 'bold' }}>
                                      {asset.is_occupied ? '👥 Yes' : '⚪ Free/Available'}
                                    </span>
                                  </td>
                                  <td>
                                    <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                                      <select
                                        className="filter-select"
                                        style={{ fontSize: '11px', padding: '2px 4px', width: '90px' }}
                                        value={asset.status}
                                        onChange={(e) => handleUpdateAssetStatus(asset.id, e.target.value)}
                                      >
                                        <option value="WORKING">Working</option>
                                        <option value="DAMAGED">Damaged</option>
                                        <option value="UNDER_REPAIR">Under Repair</option>
                                        <option value="REPLACED">Replaced</option>
                                        <option value="DISPOSED">Disposed</option>
                                      </select>
                                      <button
                                        type="button"
                                        className="btn-primary"
                                        style={{ padding: '2px 4px', fontSize: '11px', background: '#4b5563' }}
                                        onClick={() => handleViewAssetLog(asset)}
                                      >
                                        Log
                                      </button>
                                      <button
                                        type="button"
                                        className="btn-outline"
                                        style={{ padding: '2px 4px', fontSize: '11px', borderColor: '#3b82f6', color: '#2563eb' }}
                                        onClick={() => handleInitiateSwap(asset)}
                                      >
                                        Swap
                                      </button>
                                      <button
                                        type="button"
                                        className="btn-danger"
                                        style={{ padding: '2px 4px', fontSize: '11px', background: '#ef4444' }}
                                        onClick={() => handleUnallocateAsset(asset.id)}
                                      >
                                        Unmap
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Tab 3: Lifecycle Event History Logs */}
                    {roomDetailsTab === 'events' && (
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                          <h3 style={{ fontSize: '15px', fontWeight: 'bold', color: '#1f2937' }}>Immutable Asset Lifecycle Event Log</h3>
                          {selectedAssetForLog && (
                            <button className="btn-outline" style={{ padding: '2px 6px', fontSize: '11px' }} onClick={() => setSelectedAssetForLog(null)}>
                              Show All Assets
                            </button>
                          )}
                        </div>

                        {selectedAssetForLog ? (
                          <div style={{ background: '#f8fafc', padding: '10px', borderRadius: '6px', marginBottom: '10px', fontSize: '12px' }}>
                            Showing event log history for asset: <strong style={{ fontFamily: 'monospace' }}>{selectedAssetForLog.asset_label}</strong>
                          </div>
                        ) : null}

                        <div className="table-container" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                          <table className="data-table">
                            <thead>
                              <tr>
                                <th>Timestamp</th>
                                <th>Asset</th>
                                <th>Event Type</th>
                                <th>Description</th>
                                <th>Reported / Actioned By</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedRoomDetails.assets
                                .filter(asset => !selectedAssetForLog || asset.id === selectedAssetForLog.id)
                                .flatMap(asset => (asset.events || []).map(evt => ({ ...evt, asset_label: asset.asset_label })))
                                .sort((a, b) => new Date(b.created_at || b.event_date) - new Date(a.created_at || a.event_date))
                                .map((evt, idx) => (
                                  <tr key={idx}>
                                    <td style={{ fontSize: '11px', color: '#6b7280' }}>
                                      {new Date(evt.created_at || evt.event_date).toLocaleString()}
                                    </td>
                                    <td><strong style={{ fontFamily: 'monospace', fontSize: '11px' }}>{evt.asset_label}</strong></td>
                                    <td>
                                      <span className={`badge ${
                                        evt.event_type === 'INSTALLED' ? 'badge-green' : 
                                        evt.event_type === 'REPAIRED' ? 'badge-green' : 
                                        evt.event_type === 'DAMAGED' ? 'badge-red' : 
                                        evt.event_type === 'SENT_FOR_REPAIR' ? 'badge-orange' : 'badge-blue'
                                      }`} style={{ fontSize: '10px', padding: '1px 4px' }}>
                                        {evt.event_type}
                                      </span>
                                    </td>
                                    <td style={{ fontSize: '12px' }}>{evt.description}</td>
                                    <td style={{ fontSize: '11px' }}>
                                      {evt.reported_by_name || evt.reported_by || evt.action_taken_by_name || 'System'}
                                    </td>
                                  </tr>
                                ))}
                              {selectedRoomDetails.assets.reduce((sum, asset) => sum + (asset.events?.length || 0), 0) === 0 && (
                                <tr>
                                  <td colSpan="5" style={{ textAlign: 'center', fontStyle: 'italic', color: '#9ca3af' }}>
                                    No lifecycle event logs recorded yet.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Hostel Form */}
                {showModal === 'hostel' && (
                  <form onSubmit={handleHostelSubmit}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Hostel name *</label>
                        <input type="text" className="search-input" placeholder="e.g. Boys Hostel Block A, Girls Hostel Phase 2" required value={hostelForm.name} onChange={(e) => setHostelForm({ ...hostelForm, name: e.target.value })} />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Campus / Location</label>
                        <input type="text" className="search-input" placeholder="e.g. North Campus, Medical College Premises" value={hostelForm.campus} onChange={(e) => setHostelForm({ ...hostelForm, campus: e.target.value })} />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Hostel Type *</label>
                        <select className="filter-select" value={hostelForm.hostel_type} onChange={(e) => setHostelForm({ ...hostelForm, hostel_type: e.target.value })}>
                          <option value="BOYS">Boys Hostel</option>
                          <option value="GIRLS">Girls Hostel</option>
                          <option value="STAFF">Staff Hostel</option>
                          <option value="MIXED">Mixed Hostel</option>
                        </select>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Total Resident Capacity</label>
                        <input type="number" className="search-input" placeholder="e.g. 150" value={hostelForm.total_capacity} onChange={(e) => setHostelForm({ ...hostelForm, total_capacity: e.target.value })} />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Contact Mobile Phone Number</label>
                        <input type="text" className="search-input" placeholder="e.g. +91 98765 43210" value={hostelForm.contact_phone} onChange={(e) => setHostelForm({ ...hostelForm, contact_phone: e.target.value })} />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Warden Contact Email</label>
                        <input type="email" className="search-input" placeholder="e.g. warden.blocka@institution.edu" value={hostelForm.contact_email} onChange={(e) => setHostelForm({ ...hostelForm, contact_email: e.target.value })} />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Available Facilities (Comma-separated)</label>
                        <input type="text" className="search-input" placeholder="e.g. Wifi, Gym, Mess, Study Room, Hot Water" value={hostelForm.facilities} onChange={(e) => setHostelForm({ ...hostelForm, facilities: e.target.value })} />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Hostel Rules & Regulations Code of Conduct</label>
                        <textarea className="search-input" placeholder="e.g. Curfew time is 9:30 PM. Visitors must be registered. Keep rooms tidy." style={{ height: '80px' }} value={hostelForm.rules} onChange={(e) => setHostelForm({ ...hostelForm, rules: e.target.value })} />
                      </div>

                      <button type="submit" className="btn-primary">Register Hostel</button>
                    </div>
                  </form>
                )}

                {/* Building Form */}
                {showModal === 'building' && (
                  <form onSubmit={handleBuildingSubmit}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Select Target Hostel *</label>
                        <select className="filter-select" required value={buildingForm.hostel} onChange={(e) => setBuildingForm({ ...buildingForm, hostel: e.target.value })}>
                          <option value="">-- Choose Hostel --</option>
                          {hostels.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
                        </select>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Building Name / Identifier *</label>
                        <input type="text" className="search-input" placeholder="e.g. Block A, Building B, Annex Ward" required value={buildingForm.name} onChange={(e) => setBuildingForm({ ...buildingForm, name: e.target.value })} />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Wing</label>
                        <input type="text" className="search-input" placeholder="e.g. East Wing, PG Wing" value={buildingForm.wing} onChange={(e) => setBuildingForm({ ...buildingForm, wing: e.target.value })} />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Total Floors Count</label>
                        <input type="number" className="search-input" placeholder="e.g. 4" value={buildingForm.floors_count} onChange={(e) => setBuildingForm({ ...buildingForm, floors_count: e.target.value })} />
                      </div>

                      <button type="submit" className="btn-primary">Save Building</button>
                    </div>
                  </form>
                )}

                {/* Floor Form */}
                {showModal === 'floor' && (
                  <form onSubmit={handleFloorSubmit}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Select Hostel *</label>
                        <select className="filter-select" required onChange={(e) => handleHostelChange(e.target.value)}>
                          <option value="">-- Choose Hostel --</option>
                          {hostels.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
                        </select>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Select Building Block *</label>
                        <select className="filter-select" required value={floorForm.building} onChange={(e) => setFloorForm({ ...floorForm, building: e.target.value })}>
                          <option value="">-- Choose Building Block --</option>
                          {buildings.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                        </select>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Floor Number *</label>
                        <input type="number" className="search-input" placeholder="e.g. 0 for Ground, 1 for First Floor" required value={floorForm.floor_number} onChange={(e) => setFloorForm({ ...floorForm, floor_number: e.target.value })} />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Floor Name / Description</label>
                        <input type="text" className="search-input" placeholder="e.g. Ground Floor, First Floor Annex" value={floorForm.floor_name} onChange={(e) => setFloorForm({ ...floorForm, floor_name: e.target.value })} />
                      </div>

                      <button type="submit" className="btn-primary">Add Floor Level</button>
                    </div>
                  </form>
                )}

                {/* Room Form */}
                {showModal === 'room' && (
                  <form onSubmit={handleRoomSubmit}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Select Hostel *</label>
                        <select className="filter-select" required onChange={(e) => handleRoomHostelChange(e.target.value)}>
                          <option value="">-- Choose Hostel --</option>
                          {hostels.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
                        </select>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Select Building *</label>
                        <select className="filter-select" required onChange={(e) => handleBuildingChange(e.target.value)}>
                          <option value="">-- Choose Building Block --</option>
                          {buildings.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                        </select>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Select Floor Level *</label>
                        <select className="filter-select" required value={roomForm.floor} onChange={(e) => setRoomForm({ ...roomForm, floor: e.target.value })}>
                          <option value="">-- Choose Floor Level --</option>
                          {floors.map(f => <option key={f.id} value={f.id}>{f.floor_name || `Floor ${f.floor_number}`}</option>)}
                        </select>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Room Number / ID *</label>
                        <input type="text" className="search-input" placeholder="e.g. A-102, G-05" required value={roomForm.room_number} onChange={(e) => setRoomForm({ ...roomForm, room_number: e.target.value })} />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Room Type / Category *</label>
                        <select className="filter-select" value={roomForm.room_type} onChange={(e) => setRoomForm({ ...roomForm, room_type: e.target.value })}>
                          <option value="SINGLE">Single Room</option>
                          <option value="DOUBLE">Double Sharing</option>
                          <option value="TRIPLE">Triple Sharing</option>
                          <option value="DORMITORY">Dormitory Style</option>
                        </select>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Resident Capacity (Max occupants)</label>
                        <input type="number" className="search-input" placeholder="e.g. 2" value={roomForm.capacity} onChange={(e) => setRoomForm({ ...roomForm, capacity: e.target.value })} />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Monthly Rental Rent (₹)</label>
                        <input type="number" className="search-input" placeholder="e.g. 5000" value={roomForm.monthly_rent} onChange={(e) => setRoomForm({ ...roomForm, monthly_rent: e.target.value })} />
                      </div>

                      <div style={{ display: 'flex', gap: '20px', marginTop: '5px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '600' }}>
                          <input type="checkbox" checked={roomForm.has_ac} onChange={(e) => setRoomForm({ ...roomForm, has_ac: e.target.checked })} /> Air Conditioner (AC)
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '600' }}>
                          <input type="checkbox" checked={roomForm.has_attached_bath} onChange={(e) => setRoomForm({ ...roomForm, has_attached_bath: e.target.checked })} /> Attached Bath
                        </label>
                      </div>

                      {/* Available Assets — grouped checkbox checklist by accessory type */}
                      <div style={{ border: '1px solid #e5e7eb', borderRadius: '6px', padding: '12px', marginTop: '10px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                          <h4 style={{ fontSize: '13px', fontWeight: '600', color: '#374151', margin: 0 }}>
                            Allocate Available Accessories
                          </h4>
                          {(roomForm.asset_ids || []).length > 0 && (
                            <span style={{ fontSize: '11px', background: '#3b82f6', color: '#fff', borderRadius: '10px', padding: '2px 8px', fontWeight: '700' }}>
                              {(roomForm.asset_ids || []).length} selected
                            </span>
                          )}
                        </div>
                        {availableAssets.length === 0 ? (
                          <p style={{ fontSize: '12px', color: '#6b7280', fontStyle: 'italic', margin: 0 }}>
                            Select a hostel above to see available inventory.
                          </p>
                        ) : (() => {
                          const ACCESSORY_LABELS = {
                            BED: 'Beds', MATTRESS: 'Mattresses', TABLE: 'Study Tables',
                            CHAIR: 'Chairs', CUPBOARD: 'Cupboards / Wardrobes', STUDY_LAMP: 'Study Lamps',
                            AC: 'AC Units', FAN: 'Fans', LIGHT: 'Lights / Tubes',
                            CURTAIN: 'Curtains', GEYSER: 'Geysers', MIRROR: 'Mirrors',
                            DUSTBIN: 'Dustbins', BUCKET: 'Buckets', SWITCHBOARD: 'Switch Boards',
                            WATER_BOTTLE: 'Water Bottles', OTHER: 'Other',
                          };
                          const byType = {};
                          availableAssets.forEach(a => {
                            if (!byType[a.asset_type]) byType[a.asset_type] = [];
                            byType[a.asset_type].push(a);
                          });
                          const currentIds = new Set((roomForm.asset_ids || []).map(Number));
                          const toggleAsset = (assetId, checked) => {
                            const next = checked
                              ? [...(roomForm.asset_ids || []), assetId]
                              : (roomForm.asset_ids || []).filter(id => id !== assetId);
                            setRoomForm(prev => ({ ...prev, asset_ids: next }));
                          };
                          return (
                            <div style={{ maxHeight: '300px', overflowY: 'auto', paddingRight: '4px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                              {Object.entries(byType).map(([type, assets]) => (
                                <div key={type}>
                                  {/* Section header */}
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                                    <span style={{ fontSize: '11px', fontWeight: '700', color: '#4f46e5', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                      {ACCESSORY_LABELS[type] || type}
                                    </span>
                                    <span style={{ flex: 1, height: '1px', background: '#e5e7eb' }} />
                                    <span style={{ fontSize: '10px', color: '#9ca3af' }}>
                                      {assets.filter(a => currentIds.has(a.id)).length}/{assets.length} selected
                                    </span>
                                  </div>
                                  {/* Checkbox rows */}
                                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '4px' }}>
                                    {assets.map(a => (
                                      <label
                                        key={a.id}
                                        style={{
                                          display: 'flex', alignItems: 'center', gap: '7px',
                                          padding: '5px 8px', borderRadius: '5px', cursor: 'pointer',
                                          background: currentIds.has(a.id) ? '#eff6ff' : '#f9fafb',
                                          border: `1px solid ${currentIds.has(a.id) ? '#bfdbfe' : '#e5e7eb'}`,
                                          transition: 'background 0.15s',
                                        }}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={currentIds.has(a.id)}
                                          onChange={e => toggleAsset(a.id, e.target.checked)}
                                          style={{ accentColor: '#3b82f6', width: '14px', height: '14px', cursor: 'pointer' }}
                                        />
                                        <span style={{ fontFamily: 'monospace', fontSize: '11px', color: '#1f2937', fontWeight: '600' }}>
                                          {a.asset_label}
                                        </span>
                                      </label>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </div>

                      <button type="submit" className="btn-primary" style={{ marginTop: '10px' }}>Add Room</button>
                    </div>
                  </form>
                )}

                {/* Room Allocation Form */}
                {showModal === 'allocation' && (
                  <form onSubmit={handleAllocationSubmit}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Select Available Room *</label>
                        <select className="filter-select" required value={allocForm.room} onChange={(e) => setAllocForm({ ...allocForm, room: e.target.value })}>
                          <option value="">-- Choose Room --</option>
                          {rooms.map(r => <option key={r.id} value={r.id}>{r.hostel_name} — Room {r.room_number} ({r.current_occupancy}/{r.capacity} Occupied)</option>)}
                        </select>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', position: 'relative' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Search Resident Student *</label>
                        <input
                          type="text"
                          className="search-input"
                          placeholder="Type student name, username or ID..."
                          required
                          value={userSearchQuery}
                          onChange={(e) => handleUserSearchChange(e.target.value)}
                          onFocus={handleUserSearchFocus}
                          onBlur={handleUserSearchBlur}
                          autoComplete="off"
                        />
                        {isSearchingUsers && (
                          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px', paddingLeft: '5px' }}>Searching database...</div>
                        )}
                        {userSuggestions.length > 0 && (
                          <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            backgroundColor: 'white',
                            border: '1px solid #d1d5db',
                            borderRadius: '8px',
                            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                            zIndex: 1000,
                            maxHeight: '220px',
                            overflowY: 'auto',
                            marginTop: '5px'
                          }}>
                            {userSuggestions.map(s => (
                              <div
                                key={s.id}
                                onMouseDown={() => handleSelectSuggestion(s)}
                                style={{
                                  padding: '10px 14px',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f3f4f6',
                                  fontSize: '13px',
                                  color: '#1f2937',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: '2px',
                                  transition: 'background-color 0.15s ease'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              >
                                <span style={{ fontWeight: '600' }}>{s.name} ({s.username})</span>
                                <span style={{ fontSize: '11px', color: '#6b7280' }}>
                                  Role: {s.role} {s.student_id ? `| ID: ${s.student_id}` : s.employee_id ? `| ID: ${s.employee_id}` : ''}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                        {selectedResidentUser && (
                          <div style={{ fontSize: '12px', color: '#10b981', marginTop: '2px', paddingLeft: '5px', fontWeight: '500' }}>
                            ✓ Selected: {selectedResidentUser.name} ({selectedResidentUser.role})
                          </div>
                        )}
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Security Deposit Amount Paid (₹)</label>
                        <input type="number" className="search-input" placeholder="e.g. 10000" value={allocForm.security_deposit} onChange={(e) => setAllocForm({ ...allocForm, security_deposit: e.target.value })} />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Allocation Handover Notes</label>
                        <textarea className="search-input" placeholder="e.g. Keys assigned. 1 study table, 1 steel almirah handed over." value={allocForm.notes} onChange={(e) => setAllocForm({ ...allocForm, notes: e.target.value })} />
                      </div>

                      <button type="submit" className="btn-primary">Assign Allocation</button>
                    </div>
                  </form>
                )}

                {/* Outpass Form */}
                {showModal === 'outpass' && (
                  <form onSubmit={handleOutpassSubmit}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', position: 'relative' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Search Resident Student *</label>
                        <input
                          type="text"
                          className="search-input"
                          placeholder="Type resident name, username or ID..."
                          required
                          value={outpassUserSearchQuery}
                          onChange={(e) => handleOutpassUserSearchChange(e.target.value)}
                          onFocus={handleOutpassUserSearchFocus}
                          onBlur={handleOutpassUserSearchBlur}
                          autoComplete="off"
                        />
                        {isSearchingOutpassUsers && (
                          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px', paddingLeft: '5px' }}>Searching database...</div>
                        )}
                        {outpassUserSuggestions.length > 0 && (
                          <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            backgroundColor: 'white',
                            border: '1px solid #d1d5db',
                            borderRadius: '8px',
                            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                            zIndex: 1000,
                            maxHeight: '220px',
                            overflowY: 'auto',
                            marginTop: '5px'
                          }}>
                            {outpassUserSuggestions.map(s => (
                              <div
                                key={s.id}
                                onMouseDown={() => handleSelectOutpassUserSuggestion(s)}
                                style={{
                                  padding: '10px 14px',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f3f4f6',
                                  fontSize: '13px',
                                  color: '#1f2937',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: '2px',
                                  transition: 'background-color 0.15s ease'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              >
                                <span style={{ fontWeight: '600' }}>{s.name} ({s.username})</span>
                                <span style={{ fontSize: '11px', color: '#6b7280' }}>
                                  Role: {s.role} {s.student_id ? `| ID: ${s.student_id}` : s.employee_id ? `| ID: ${s.employee_id}` : ''}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                        {selectedOutpassUser && (
                          <div style={{ fontSize: '12px', color: '#10b981', marginTop: '2px', paddingLeft: '5px', fontWeight: '500' }}>
                            ✓ Selected: {selectedOutpassUser.name} ({selectedOutpassUser.role})
                          </div>
                        )}
                      </div>

                      <div style={{ display: 'flex', gap: '15px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', flex: 1 }}>
                          <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Out Date *</label>
                          <input 
                            type="date" 
                            className="search-input" 
                            required 
                            value={outpassForm.out_date} 
                            onChange={(e) => setOutpassForm({ ...outpassForm, out_date: e.target.value })} 
                          />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', flex: 1 }}>
                          <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Out Time *</label>
                          <input 
                            type="time" 
                            className="search-input" 
                            required 
                            value={outpassForm.out_time} 
                            onChange={(e) => setOutpassForm({ ...outpassForm, out_time: e.target.value })} 
                          />
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: '15px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', flex: 1 }}>
                          <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Expected Return Date *</label>
                          <input 
                            type="date" 
                            className="search-input" 
                            required 
                            value={outpassForm.expected_return_date} 
                            onChange={(e) => setOutpassForm({ ...outpassForm, expected_return_date: e.target.value })} 
                          />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', flex: 1 }}>
                          <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Expected Return Time *</label>
                          <input 
                            type="time" 
                            className="search-input" 
                            required 
                            value={outpassForm.expected_return_time} 
                            onChange={(e) => setOutpassForm({ ...outpassForm, expected_return_time: e.target.value })} 
                          />
                        </div>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Reason for Outpass *</label>
                        <textarea 
                          className="search-input" 
                          placeholder="e.g. Home visit, medical checkup, weekend outing..." 
                          required
                          value={outpassForm.reason} 
                          onChange={(e) => setOutpassForm({ ...outpassForm, reason: e.target.value })} 
                        />
                      </div>

                      <button type="submit" className="btn-primary" style={{ marginTop: '10px' }}>Issue Outpass Ticket</button>
                    </div>
                  </form>
                )}

                {/* Visitor Form */}
                {showModal === 'visitor' && (
                  <form onSubmit={handleVisitorSubmit}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', position: 'relative' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Search Resident Student Being Visited *</label>
                        <input
                          type="text"
                          className="search-input"
                          placeholder="Type student name, username or ID..."
                          required
                          value={visitorSearchQuery}
                          onChange={(e) => handleVisitorSearchChange(e.target.value)}
                          onFocus={handleVisitorSearchFocus}
                          onBlur={handleVisitorSearchBlur}
                          autoComplete="off"
                        />
                        {isSearchingVisitors && (
                          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px', paddingLeft: '5px' }}>Searching database...</div>
                        )}
                        {visitorSuggestions.length > 0 && (
                          <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            backgroundColor: 'white',
                            border: '1px solid #d1d5db',
                            borderRadius: '8px',
                            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                            zIndex: 1000,
                            maxHeight: '220px',
                            overflowY: 'auto',
                            marginTop: '5px'
                          }}>
                            {visitorSuggestions.map(s => (
                              <div
                                key={s.id}
                                onMouseDown={() => handleSelectVisitorSuggestion(s)}
                                style={{
                                  padding: '10px 14px',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f3f4f6',
                                  fontSize: '13px',
                                  color: '#1f2937',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: '2px',
                                  transition: 'background-color 0.15s ease'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              >
                                <span style={{ fontWeight: '600' }}>{s.name} ({s.username})</span>
                                <span style={{ fontSize: '11px', color: '#6b7280' }}>
                                  Role: {s.role} {s.student_id ? `| ID: ${s.student_id}` : s.employee_id ? `| ID: ${s.employee_id}` : ''}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                        {selectedVisitorUser && (
                          <div style={{ fontSize: '12px', color: '#10b981', marginTop: '2px', paddingLeft: '5px', fontWeight: '500' }}>
                            ✓ Selected: {selectedVisitorUser.name} ({selectedVisitorUser.role})
                          </div>
                        )}
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Visitor Full Name *</label>
                        <input type="text" className="search-input" placeholder="e.g. Rajesh Kumar" required value={visitorForm.visitor_name} onChange={(e) => setVisitorForm({ ...visitorForm, visitor_name: e.target.value })} />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Visitor Mobile Number *</label>
                        <input type="text" className="search-input" placeholder="e.g. 98765 43210" required value={visitorForm.visitor_mobile} onChange={(e) => setVisitorForm({ ...visitorForm, visitor_mobile: e.target.value })} />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Relationship to Resident Student *</label>
                        <input type="text" className="search-input" placeholder="e.g. Parent, Sibling, Uncle" required value={visitorForm.relationship} onChange={(e) => setVisitorForm({ ...visitorForm, relationship: e.target.value })} />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>ID Proof Type</label>
                        <input type="text" className="search-input" placeholder="e.g. Aadhaar Card, Driving License" value={visitorForm.id_proof_type} onChange={(e) => setVisitorForm({ ...visitorForm, id_proof_type: e.target.value })} />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>ID Proof Number / Code</label>
                        <input type="text" className="search-input" placeholder="e.g. XXXX-XXXX-XXXX" value={visitorForm.id_proof_number} onChange={(e) => setVisitorForm({ ...visitorForm, id_proof_number: e.target.value })} />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Purpose of Visit</label>
                        <textarea className="search-input" placeholder="e.g. Personal meet, Delivery of essential items" value={visitorForm.purpose} onChange={(e) => setVisitorForm({ ...visitorForm, purpose: e.target.value })} />
                      </div>

                      <button type="submit" className="btn-primary">Check-in Visitor</button>
                    </div>
                  </form>
                )}

                {/* Leave Application Form */}
                {showModal === 'leave' && (
                  <form onSubmit={handleLeaveSubmit}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Leave Type / Pass Category *</label>
                        <select className="filter-select" value={leaveForm.leave_type} onChange={(e) => setLeaveForm({ ...leaveForm, leave_type: e.target.value })}>
                          <option value="DAY">Day Pass Outpass</option>
                          <option value="WEEKEND">Weekend Night Stay</option>
                          <option value="EMERGENCY">Emergency Leave Out</option>
                          <option value="VACATION">Vacation Hold</option>
                        </select>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>From Date *</label>
                        <input type="date" className="search-input" style={{ width: '100%' }} required value={leaveForm.from_date} onChange={(e) => setLeaveForm({ ...leaveForm, from_date: e.target.value })} />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>To Date *</label>
                        <input type="date" className="search-input" style={{ width: '100%' }} required value={leaveForm.to_date} onChange={(e) => setLeaveForm({ ...leaveForm, to_date: e.target.value })} />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Destination Address *</label>
                        <input type="text" className="search-input" placeholder="e.g. 123 Main Road, Home Town City" required value={leaveForm.destination} onChange={(e) => setLeaveForm({ ...leaveForm, destination: e.target.value })} />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Parent / Guardian Contact Mobile *</label>
                        <input type="text" className="search-input" placeholder="e.g. 98765-43210" required value={leaveForm.parent_contact} onChange={(e) => setLeaveForm({ ...leaveForm, parent_contact: e.target.value })} />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Reason for Leave *</label>
                        <textarea className="search-input" placeholder="e.g. Family marriage, Going home for weekend" required value={leaveForm.reason} onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })} />
                      </div>

                      <button type="submit" className="btn-primary">Submit Request</button>
                    </div>
                  </form>
                )}

                {/* Complaint Submission Form */}
                {showModal === 'complaint' && (
                  <form onSubmit={handleComplaintSubmit}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Complaint Title / Short Summary *</label>
                        <input type="text" className="search-input" placeholder="e.g. Broken Fan Regulator, Water Leakage" required value={complaintForm.title} onChange={(e) => setComplaintForm({ ...complaintForm, title: e.target.value })} />
                      </div>

                      {isStaff && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                          <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Select Target Room *</label>
                          <select className="filter-select" required value={complaintForm.room} onChange={(e) => setComplaintForm({ ...complaintForm, room: e.target.value })}>
                            <option value="">-- Choose Room --</option>
                            {rooms.map(r => <option key={r.id} value={r.id}>{r.hostel_name} — Room {r.room_number}</option>)}
                          </select>
                        </div>
                      )}

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Issue Category *</label>
                        <select className="filter-select" value={complaintForm.category} onChange={(e) => setComplaintForm({ ...complaintForm, category: e.target.value })}>
                          <option value="ELECTRICAL">Electrical Issue</option>
                          <option value="PLUMBING">Plumbing Issue</option>
                          <option value="FURNITURE">Furniture Broken / Repair</option>
                          <option value="CLEANING">Cleaning Request</option>
                          <option value="INTERNET">Internet Wifi Issue</option>
                          <option value="WATER">Water Supply Issue</option>
                          <option value="AC_COOLING">AC & Cooling Issue</option>
                          <option value="SECURITY">Security / Safety Issue</option>
                          <option value="OTHER">Other Issues</option>
                        </select>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Priority Level *</label>
                        <select className="filter-select" value={complaintForm.priority} onChange={(e) => setComplaintForm({ ...complaintForm, priority: e.target.value })}>
                          <option value="LOW">Low priority</option>
                          <option value="MEDIUM">Medium priority</option>
                          <option value="HIGH">High priority</option>
                        </select>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Detailed Description of the Issue *</label>
                        <textarea className="search-input" placeholder="e.g. Ceiling fan regulator in room A-102 is broken and running on maximum speed." required value={complaintForm.description} onChange={(e) => setComplaintForm({ ...complaintForm, description: e.target.value })} />
                      </div>

                      <button type="submit" className="btn-primary">Submit Ticket</button>
                    </div>
                  </form>
                )}

                {/* Billing / Fee Form */}
                {showModal === 'fee' && (
                  <form onSubmit={handleFeeSubmit}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Student Username *</label>
                        <input type="text" className="search-input" placeholder="e.g. mbbs1st1" required value={feeForm.resident_username} onChange={(e) => setFeeForm({ ...feeForm, resident_username: e.target.value })} />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Fee Type / Invoice Category *</label>
                        <select className="filter-select" value={feeForm.fee_type} onChange={(e) => setFeeForm({ ...feeForm, fee_type: e.target.value })}>
                          <option value="HOSTEL">Hostel Rent Bill</option>
                          <option value="SECURITY">Security Deposit</option>
                          <option value="MESS">Mess Monthly charges</option>
                          <option value="ELECTRICITY">Electricity Bill</option>
                          <option value="MAINTENANCE">Maintenance Charges</option>
                          <option value="FINE">Fine / Late curfew charge</option>
                        </select>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Bill Amount (₹) *</label>
                        <input type="number" className="search-input" placeholder="e.g. 4500" required value={feeForm.amount} onChange={(e) => setFeeForm({ ...feeForm, amount: e.target.value })} />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Due Date *</label>
                        <input type="date" className="search-input" style={{ width: '100%' }} required value={feeForm.due_date} onChange={(e) => setFeeForm({ ...feeForm, due_date: e.target.value })} />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Billing Month & Year</label>
                        <input type="text" className="search-input" placeholder="e.g. July 2026" value={feeForm.month_year} onChange={(e) => setFeeForm({ ...feeForm, month_year: e.target.value })} />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Fee Invoice Remarks / Notes</label>
                        <textarea className="search-input" placeholder="e.g. Monthly room rent including AC charges." value={feeForm.notes} onChange={(e) => setFeeForm({ ...feeForm, notes: e.target.value })} />
                      </div>

                      <button type="submit" className="btn-primary">Generate Invoice</button>
                    </div>
                  </form>
                )}

                {/* Vendor Form */}
                {showModal === 'vendor' && (
                  <form onSubmit={handleVendorSubmit}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Company / Vendor Business Name *</label>
                        <input type="text" className="search-input" placeholder="e.g. MedEquip Technologies Pvt Ltd" required value={vendorForm.name} onChange={(e) => setVendorForm({ ...vendorForm, name: e.target.value })} />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Contact Person Name</label>
                        <input type="text" className="search-input" placeholder="e.g. Anil Kumar" value={vendorForm.contact_person} onChange={(e) => setVendorForm({ ...vendorForm, contact_person: e.target.value })} />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Vendor Contact Email ID</label>
                        <input type="email" className="search-input" placeholder="e.g. sales@medequip.com" value={vendorForm.email} onChange={(e) => setVendorForm({ ...vendorForm, email: e.target.value })} />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Vendor Contact Mobile Number</label>
                        <input type="text" className="search-input" placeholder="e.g. +91 99988 77766" value={vendorForm.phone} onChange={(e) => setVendorForm({ ...vendorForm, phone: e.target.value })} />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Business GST Number</label>
                        <input type="text" className="search-input" placeholder="e.g. 29AAAAA1111A1Z1" value={vendorForm.gst_number} onChange={(e) => setVendorForm({ ...vendorForm, gst_number: e.target.value })} />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Payment Terms / Conditions</label>
                        <input type="text" className="search-input" placeholder="e.g. Net 30, 50% Advance" value={vendorForm.payment_terms} onChange={(e) => setVendorForm({ ...vendorForm, payment_terms: e.target.value })} />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Vendor Office Address & Notes</label>
                        <textarea className="search-input" placeholder="e.g. Industrial Area, Phase II, Bengaluru." value={vendorForm.notes} onChange={(e) => setVendorForm({ ...vendorForm, notes: e.target.value })} />
                      </div>

                      <button type="submit" className="btn-primary">Register Vendor</button>
                    </div>
                  </form>
                )}

                {/* Asset Form */}
                {showModal === 'asset' && (
                  <form onSubmit={handleAssetSubmit}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Asset Name *</label>
                        <input type="text" className="search-input" placeholder="e.g. Lab Microscope, Office Desk, AC Unit" required value={assetForm.name} onChange={(e) => setAssetForm({ ...assetForm, name: e.target.value })} />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Unique Asset Code / Stock Code *</label>
                        <input type="text" className="search-input" placeholder="e.g. INST-MIC-042" required value={assetForm.asset_code} onChange={(e) => setAssetForm({ ...assetForm, asset_code: e.target.value })} />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Barcode ID (Optional)</label>
                        <input type="text" className="search-input" placeholder="e.g. 1004032" value={assetForm.barcode} onChange={(e) => setAssetForm({ ...assetForm, barcode: e.target.value })} />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Select Asset Category *</label>
                          <button 
                            type="button" 
                            style={{ 
                              background: 'none', 
                              border: 'none', 
                              color: '#2563eb', 
                              fontSize: '11px', 
                              cursor: 'pointer', 
                              fontWeight: '600',
                              padding: 0 
                            }} 
                            onClick={async () => {
                              const catName = prompt('Enter new category name:');
                              if (catName) {
                                try {
                                  const newCat = await hostelInventoryAPI.createCategory({ name: catName, description: 'Category for ' + catName });
                                  const updatedCats = await hostelInventoryAPI.getCategories();
                                  setCategories(updatedCats.results || updatedCats);
                                  setAssetForm(prev => ({ ...prev, category: newCat.id }));
                                  notify.success('Category "' + catName + '" created successfully.');
                                } catch (err) {
                                  notify.error(err.response?.data?.error || 'Failed to create category.');
                                }
                              }
                            }}
                          >
                            + Add New Category
                          </button>
                        </div>
                        <select className="filter-select" required value={assetForm.category} onChange={(e) => setAssetForm({ ...assetForm, category: e.target.value })}>
                          <option value="">-- Choose Category --</option>
                          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Select Institutional Department (Optional)</label>
                        <select className="filter-select" value={assetForm.department} onChange={(e) => setAssetForm({ ...assetForm, department: e.target.value })}>
                          <option value="">-- Storage Room / None --</option>
                          {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                        </select>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Select Registered Vendor</label>
                        <select className="filter-select" value={assetForm.vendor} onChange={(e) => setAssetForm({ ...assetForm, vendor: e.target.value })}>
                          <option value="">-- Choose Vendor --</option>
                          {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                        </select>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Purchase Cost Price (₹) *</label>
                        <input type="number" className="search-input" placeholder="e.g. 18500" required value={assetForm.purchase_price} onChange={(e) => setAssetForm({ ...assetForm, purchase_price: e.target.value })} />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Purchase Date</label>
                        <input type="date" className="search-input" style={{ width: '100%' }} value={assetForm.purchase_date} onChange={(e) => setAssetForm({ ...assetForm, purchase_date: e.target.value })} />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Current Placement Location / Placement Room</label>
                        <input type="text" className="search-input" placeholder="e.g. Bio-Chemistry Lab 3, Hostel Block B Study" value={assetForm.current_location} onChange={(e) => setAssetForm({ ...assetForm, current_location: e.target.value })} />
                      </div>

                      <div style={{ display: 'flex', gap: '20px', marginTop: '5px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '600' }}>
                          <input type="checkbox" checked={assetForm.is_consumable} onChange={(e) => setAssetForm({ ...assetForm, is_consumable: e.target.checked })} /> Consumable Stock Item (e.g. syringe, papers)
                        </label>
                      </div>

                      <button type="submit" className="btn-primary">Register Asset Record</button>
                    </div>
                  </form>
                )}



                {/* Stock transaction Form */}
                {showModal === 'stock' && (
                  <form onSubmit={handleStockSubmit}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Select Asset / Stock Item *</label>
                        <select className="filter-select" required value={stockForm.item} onChange={(e) => setStockForm({ ...stockForm, item: e.target.value })}>
                          <option value="">-- Choose Item --</option>
                          {items.map(item => <option key={item.id} value={item.id}>[{item.asset_code}] {item.name}</option>)}
                        </select>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Transaction Flow Type *</label>
                        <select className="filter-select" value={stockForm.transaction_type} onChange={(e) => setStockForm({ ...stockForm, transaction_type: e.target.value })}>
                          <option value="IN">Stock In (Purchase/Restock)</option>
                          <option value="OUT">Stock Out (Allocated)</option>
                          <option value="DAMAGE">Damaged / Disposed</option>
                        </select>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Quantity *</label>
                        <input type="number" className="search-input" placeholder="e.g. 10" required value={stockForm.quantity} onChange={(e) => setStockForm({ ...stockForm, quantity: e.target.value })} />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Source Location</label>
                        <input type="text" className="search-input" placeholder="e.g. Vendor Name, Store Room A" value={stockForm.from_location} onChange={(e) => setStockForm({ ...stockForm, from_location: e.target.value })} />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Destination Location</label>
                        <input type="text" className="search-input" placeholder="e.g. Central Pharmacy, Biochemistry Lab" value={stockForm.to_location} onChange={(e) => setStockForm({ ...stockForm, to_location: e.target.value })} />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Reference Document / Invoice Number</label>
                        <input type="text" className="search-input" placeholder="e.g. INV-10024" value={stockForm.reference_number} onChange={(e) => setStockForm({ ...stockForm, reference_number: e.target.value })} />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Audit Remarks / Transaction Notes</label>
                        <textarea className="search-input" placeholder="e.g. Restocked syringe boxes. Expiry date matches invoices." value={stockForm.notes} onChange={(e) => setStockForm({ ...stockForm, notes: e.target.value })} />
                      </div>

                      <button type="submit" className="btn-primary">Log Transaction</button>
                    </div>
                  </form>
                )}

                {/* Asset Handover Assignment Form */}
                {showModal === 'assignment' && (
                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    try {
                      const userRes = await api.get(`/auth/principal/users/?search=${allocForm.resident_username}`);
                      const matchedUser = userRes.data.results?.find(u => u.username === allocForm.resident_username);
                      if (!matchedUser) {
                        setError('User username not found');
                        return;
                      }
                      await hostelInventoryAPI.createAssignment({
                        item: roomForm.floor, // hijacked roomForm.floor for selected item ID
                        assigned_to: matchedUser.id,
                        assigned_date: new Date().toISOString().split('T')[0],
                        notes: allocForm.notes
                      });
                      setSuccess('Asset assigned successfully');
                      setShowModal(null);
                      fetchData();
                    } catch (err) {
                      setError('Failed to assign asset');
                    }
                  }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Select Available Asset to Hand Over *</label>
                        <select className="filter-select" required onChange={(e) => setRoomForm({ ...roomForm, floor: e.target.value })}>
                          <option value="">-- Choose Asset from Available Stock --</option>
                          {items.filter(i => i.status === 'AVAILABLE').map(i => <option key={i.id} value={i.id}>[{i.asset_code}] {i.name}</option>)}
                        </select>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', position: 'relative' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Assignee User (Hostel Resident) *</label>
                        <input
                          type="text"
                          className="search-input"
                          placeholder="Search allocated resident by name, username or ID..."
                          required
                          value={handoverSearchQuery}
                          onChange={(e) => handleHandoverSearchChange(e.target.value)}
                          onFocus={handleHandoverSearchFocus}
                          onBlur={handleHandoverSearchBlur}
                          autoComplete="off"
                        />
                        {isSearchingHandoverUsers && (
                          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px', paddingLeft: '5px' }}>Searching database...</div>
                        )}
                        {handoverSuggestions.length > 0 && (
                          <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            backgroundColor: 'white',
                            border: '1px solid #d1d5db',
                            borderRadius: '8px',
                            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                            zIndex: 1000,
                            maxHeight: '220px',
                            overflowY: 'auto',
                            marginTop: '5px'
                          }}>
                            {handoverSuggestions.map(s => (
                              <div
                                key={s.id}
                                onMouseDown={() => handleSelectHandoverSuggestion(s)}
                                style={{
                                  padding: '10px 14px',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f3f4f6',
                                  fontSize: '13px',
                                  color: '#1f2937',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: '2px',
                                  transition: 'background-color 0.15s ease'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              >
                                <span style={{ fontWeight: '600' }}>{s.name} ({s.username})</span>
                                <span style={{ fontSize: '11px', color: '#6b7280' }}>
                                  Room: {s.room_number || 'N/A'} | Role: {s.role} {s.student_id ? `| ID: ${s.student_id}` : s.employee_id ? `| ID: ${s.employee_id}` : ''}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                        {/* Hidden input to satisfy the required constraint */}
                        <input type="hidden" name="resident_username" required value={allocForm.resident_username} />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Assignment Handover Remarks</label>
                        <textarea className="search-input" placeholder="e.g. Handed over key + user manuals. Condition verified." value={allocForm.notes} onChange={(e) => setAllocForm({ ...allocForm, notes: e.target.value })} />
                      </div>

                      <button type="submit" className="btn-primary">Handover Asset</button>
                    </div>
                  </form>
                )}

                {/* Start Repair Form */}
                {showModal === 'startRepair' && selectedComplaint && (
                  <form onSubmit={(e) => {
                    e.preventDefault();
                    handleStartComplaintRepair(selectedComplaint.id, repairForm);
                  }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Repair Start Date *</label>
                        <input
                          type="date"
                          className="search-input"
                          required
                          value={repairForm.repair_started_date}
                          onChange={(e) => setRepairForm({ ...repairForm, repair_started_date: e.target.value })}
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Repair Remarks / Instructions</label>
                        <textarea
                          className="search-input"
                          placeholder="e.g. Assigned to internal electrician. Checking wiring."
                          value={repairForm.repair_remarks}
                          onChange={(e) => setRepairForm({ ...repairForm, repair_remarks: e.target.value })}
                        />
                      </div>
                      <button type="submit" className="btn-primary">🔧 Start Repair Work</button>
                    </div>
                  </form>
                )}

                {/* Complete Repair Form */}
                {showModal === 'completeRepair' && selectedComplaint && (
                  <form onSubmit={(e) => {
                    e.preventDefault();
                    handleCompleteComplaintRepair(selectedComplaint.id, repairCompleteForm);
                  }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Repair Completed Date *</label>
                        <input
                          type="date"
                          className="search-input"
                          required
                          value={repairCompleteForm.repair_completed_date}
                          onChange={(e) => setRepairCompleteForm({ ...repairCompleteForm, repair_completed_date: e.target.value })}
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Repair Action Remarks</label>
                        <textarea
                          className="search-input"
                          placeholder="e.g. Replaced capacitor, motor cleaned."
                          value={repairCompleteForm.repair_remarks}
                          onChange={(e) => setRepairCompleteForm({ ...repairCompleteForm, repair_remarks: e.target.value })}
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Resolution Notes (Visible to Student) *</label>
                        <textarea
                          className="search-input"
                          required
                          placeholder="e.g. The fan has been repaired and is working fine now."
                          value={repairCompleteForm.resolution_notes}
                          onChange={(e) => setRepairCompleteForm({ ...repairCompleteForm, resolution_notes: e.target.value })}
                        />
                      </div>
                      <button type="submit" className="btn-primary">✅ Complete Repair & Close</button>
                    </div>
                  </form>
                )}

                {/* Request Replacement Form */}
                {showModal === 'requestReplacement' && selectedComplaint && (
                  <form onSubmit={(e) => {
                    e.preventDefault();
                    handleRequestComplaintReplacement(selectedComplaint.id, replacementForm);
                  }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Accessory Type *</label>
                        <select
                          className="filter-select"
                          required
                          value={replacementForm.accessory_type}
                          onChange={(e) => setReplacementForm({ ...replacementForm, accessory_type: e.target.value })}
                        >
                          <option value="BED">Bed</option>
                          <option value="FAN">Fan</option>
                          <option value="LIGHT">Light</option>
                          <option value="CHAIR">Chair</option>
                          <option value="TABLE">Table</option>
                          <option value="AC">AC</option>
                          <option value="OTHER">Other Accessory</option>
                        </select>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Quantity *</label>
                        <input
                          type="number"
                          min="1"
                          className="search-input"
                          required
                          value={replacementForm.quantity}
                          onChange={(e) => setReplacementForm({ ...replacementForm, quantity: parseInt(e.target.value) })}
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Reason for Replacement *</label>
                        <textarea
                          className="search-input"
                          required
                          placeholder="e.g. Asset is beyond repair, burnt motor."
                          value={replacementForm.reason}
                          onChange={(e) => setReplacementForm({ ...replacementForm, reason: e.target.value })}
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Additional Remarks / Specifications</label>
                        <textarea
                          className="search-input"
                          placeholder="e.g. Requires 48-inch ceiling fan high speed."
                          value={replacementForm.remarks}
                          onChange={(e) => setReplacementForm({ ...replacementForm, remarks: e.target.value })}
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Priority *</label>
                        <select
                          className="filter-select"
                          required
                          value={replacementForm.priority}
                          onChange={(e) => setReplacementForm({ ...replacementForm, priority: e.target.value })}
                        >
                          <option value="LOW">Low</option>
                          <option value="MEDIUM">Medium</option>
                          <option value="HIGH">High</option>
                        </select>
                      </div>
                      <button type="submit" className="btn-primary">📦 Submit Replacement Request</button>
                    </div>
                  </form>
                )}

                {/* Install Asset Form */}
                {showModal === 'installAsset' && selectedHpr && (
                  <form onSubmit={(e) => {
                    e.preventDefault();
                    handleInstallHostelAsset(selectedHpr.id, installForm);
                  }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>New Unique Asset Label / Code *</label>
                        <input
                          type="text"
                          className="search-input"
                          required
                          readOnly
                          style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                          value={installForm.new_asset_label}
                        />
                        <span style={{ fontSize: '11px', color: '#6b7280' }}>
                          This unique label has been automatically generated upon finance purchase.
                        </span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Installation Remarks</label>
                        <textarea
                          className="search-input"
                          placeholder="e.g. Mounted on ceiling, tested and working."
                          value={installForm.remarks}
                          onChange={(e) => setInstallForm({ ...installForm, remarks: e.target.value })}
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Resolution Notes (for linking Complaint) *</label>
                        <textarea
                          className="search-input"
                          required
                          placeholder="e.g. Replaced damaged fan with brand new unit. Verified working."
                          value={installForm.resolution_notes}
                          onChange={(e) => setInstallForm({ ...installForm, resolution_notes: e.target.value })}
                        />
                      </div>
                      <button type="submit" className="btn-primary">🔧 Complete Installation</button>
                    </div>
                  </form>
                )}

                {/* Swap Asset Form */}
                {showModal === 'swapAsset' && swappingAsset && (
                  <form onSubmit={handleCompleteSwap}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                      <p style={{ fontSize: '13px', color: '#4b5563' }}>
                        Swapping asset <strong style={{ fontFamily: 'monospace' }}>{swappingAsset.asset_label}</strong> ({swappingAsset.asset_type_display || swappingAsset.asset_type}) currently in Room {selectedRoomDetails.room.room_number}.
                      </p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Select Replacement Asset from Inventory *</label>
                        <select
                          className="filter-select"
                          required
                          value={selectedSwapTarget}
                          onChange={(e) => setSelectedSwapTarget(e.target.value)}
                        >
                          <option value="">-- Choose Available Asset --</option>
                          {swapOptions.map(opt => (
                            <option key={opt.id} value={opt.id}>
                              {opt.asset_label} (SN: {opt.asset_number || 'N/A'})
                            </option>
                          ))}
                        </select>
                      </div>
                      <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                        <button type="submit" className="btn-primary" style={{ flex: 1 }}>Confirm Swap</button>
                        <button type="button" className="btn-outline" style={{ flex: 1 }} onClick={() => setShowModal('roomDetail')}>Cancel</button>
                      </div>
                    </div>
                  </form>
                )}

                {/* Request Hostel Accessories Procurement Form */}
                {showModal === 'hostelPr' && (
                  <form onSubmit={handleHostelPrSubmit}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Select Hostel *</label>
                        <select
                          className="filter-select"
                          required
                          value={hostelPrForm.hostel}
                          onChange={(e) => setHostelPrForm({ ...hostelPrForm, hostel: e.target.value })}
                        >
                          <option value="">-- Choose Hostel --</option>
                          {hostels.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
                        </select>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Accessory Type *</label>
                        <select
                          className="filter-select"
                          required
                          value={hostelPrForm.accessory_type}
                          onChange={(e) => setHostelPrForm({ ...hostelPrForm, accessory_type: e.target.value })}
                        >
                          <option value="BED">Bed</option>
                          <option value="MATTRESS">Mattress</option>
                          <option value="FAN">Fan</option>
                          <option value="LIGHT">Light/Tube</option>
                          <option value="TABLE">Study Table</option>
                          <option value="CHAIR">Study Chair</option>
                          <option value="CUPBOARD">Cupboard/Wardrobe</option>
                          <option value="AC">AC Unit</option>
                          <option value="BUCKET">Bucket</option>
                          <option value="MIRROR">Mirror</option>
                          <option value="CURTAIN">Curtain</option>
                          <option value="DUSTBIN">Dustbin</option>
                          <option value="GEYSER">Geyser</option>
                          <option value="STUDY_LAMP">Study Lamp</option>
                          <option value="SWITCHBOARD">Switch Board</option>
                          <option value="OTHER">Other</option>
                        </select>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Quantity *</label>
                        <input
                          type="number"
                          min="1"
                          className="search-input"
                          required
                          value={hostelPrForm.quantity}
                          onChange={(e) => setHostelPrForm({ ...hostelPrForm, quantity: parseInt(e.target.value) || 1 })}
                        />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Priority *</label>
                        <select
                          className="filter-select"
                          required
                          value={hostelPrForm.priority}
                          onChange={(e) => setHostelPrForm({ ...hostelPrForm, priority: e.target.value })}
                        >
                          <option value="LOW">Low</option>
                          <option value="MEDIUM">Medium</option>
                          <option value="HIGH">High</option>
                        </select>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Reason / Justification *</label>
                        <textarea
                          className="search-input"
                          required
                          placeholder="e.g. Need additional chairs for study rooms"
                          value={hostelPrForm.reason}
                          onChange={(e) => setHostelPrForm({ ...hostelPrForm, reason: e.target.value })}
                        />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Remarks (Optional)</label>
                        <textarea
                          className="search-input"
                          placeholder="e.g. Budget approved in annual meet."
                          value={hostelPrForm.remarks}
                          onChange={(e) => setHostelPrForm({ ...hostelPrForm, remarks: e.target.value })}
                        />
                      </div>

                      <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                        <button type="submit" className="btn-primary" style={{ flex: 1 }}>Submit Request</button>
                        <button type="button" className="btn-outline" style={{ flex: 1 }} onClick={() => setShowModal(null)}>Cancel</button>
                      </div>
                    </div>
                  </form>
                )}

              </div>
            </div>
          </div>
        )}

      </div>
    </DashboardLayout>
  );
};

export default HostelDashboard;